import { useCallback, useContext, useEffect, useState, useRef } from 'react';
import {
  CircularProgress as MuiCircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Zoom,
  DialogContentText,
  DialogTitle,
  IconButton,
  Button,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import Spinner from 'Components/Spinner';
import CloseIcon from '@mui/icons-material/Close';
import {
  axios,
  baseUrl,
  dataFetcher,
  endpoints,
  firebaseCredentials,
  cloudBaseUrl,
} from 'Api';
import { getAllActivePlans, addVideo, subscribeToUploadStatus } from 'Api/apiFunctions';
import { logAppEvent } from 'Api/errorLogger';
import { ALLOWED_UPLOADS_COUNT, PLANS, encryptedKeyForCredentials } from 'Commons';
import { Snackbar } from 'Components';
import { ToastContainer, toast } from 'react-toastify';
import noPhotoAvailable from 'Assets/no-image-available.png';
import ForNoImage from 'Assets/Placeholder.png';
import 'react-toastify/dist/ReactToastify.css';
import Referral from 'Pages/private/Referral';
import ListingModal from 'Components/ListingModal';
import { AppContext, AuthContext } from 'Context';
import {
  isLifetimePlan,
  isOnboardingPath,
  isPaymentGateExemptPath,
  isSubscriptionForCurrentAccount,
  needsPaymentMethod,
  ownsItsOwnBilling,
  stampSubscriptionAccount,
} from 'Utils/paymentGate';
import { setSharedFeatureEnabled, setThumbnailTileStyle } from 'Config/featureFlags';
import { clearOnboardingBannerState } from 'Utils/onboardingChecklist';
import { normalizeBg } from 'hooks/useProfileBg';
import { clearUiLane, refreshAllowedEmails } from 'hooks/useNewUI';
import { Provider, useSelector, useDispatch } from 'react-redux';
import { saveUploadInfo, saveUploadDetails, saveTitle, saveDummy, removeDummy, removeTitle } from 'store/store';
import { initializeApp } from 'firebase/app';
import { ref, getDatabase, off, onValue, update } from 'firebase/database';
import AWS from 'aws-sdk';
import { awsConfig, generateS3Url, getBucketName, getBucketNamePath } from 'Api/awsConfig';
import 'Utils/debugToken'; // Token debugging utility (available as window.debugToken())
import 'Api/signinIntegration'; // Signin helper (available as window.saveTokensFromResponse())
import {
  Access,
  AccountSettings,
  AlbumDetails,
  AlbumPlayer,
  BillingSettings,
  CloudSettings,
  ConnectedAccountSettings,
  ContentLayout,
  ContentLayoutDashboard,
  ContentLayoutDetails,
  Dashboard,
  Storage,
  EmailVerification,
  EmailVerificationLink,
  ForgotPassword,
  SetPassword,
  GettingStarted,
  Groups,
  LandingPage,
  ManageDevice,
  ManagePaymentMethods,
  MobileVerification,
  NotificationSettings,
  Privacy,
  PrivacySettings,
  Profile,
  PublicPlans,
  SearchPage,
  SignUp,
  SubscriberAgreementPage,
  SubscriberAgreementSettings,
  Successors,
  ConnectedAccounts,
  TermsAndConditionSettings,
  TermsAndConditions,
  TermsAndServices,
  VerifyCode,
  VideoDetails,
  VideoPlayer,
  Viewers,
  Watchlist,
  YourVideos,
} from 'Pages';
import { InnerPage } from 'Pages/private/innerpage';
import { SharedItem } from 'Pages/public/shared-item';
import { ViewerInvite } from 'Pages/viewer-invite';
import { NewMobileVerification } from 'Pages/public/authentication/newMobile-verification';
import { NewSignUP } from 'Pages/public/authentication/newSign-Up';
import { NewSignIn } from 'Pages/public/authentication/newSign-in';
import { Welcome } from 'Pages/public/authentication/Trail/Welcome';
import { QuickUserWelcome } from 'Pages/public/authentication/Trail/QuickUserWelcome';
import { QuickUserPayment } from 'Pages/public/authentication/Trail/QuickUserPayment';
import { NewVerifyCode } from 'Pages/public/authentication/newVerification-code';
import { GiftFlowIntro } from 'Pages/public/gift-flow';
import { ContributePage } from 'Pages/public/contribute';
import { ViewersLayoutSwitch } from 'Pages/private/viewers/components/page/ViewersLayout';
import { ProjectorStreamHome } from 'Pages/public/projector-stream-home';
import { ReceivingGift } from 'Pages/public/receiving-gift';
import { GiftPayment } from 'Pages/public/gift-payment';
import { GiftContent } from 'Pages/public/gift-content';
import MobileCameraPage from 'Pages/public/mobile-camera';
import { Pathname } from 'Routes';
import 'Styles/site-global.css';
import './Pages/public/landing-external2/App.css';
import detectBrowserLanguage from 'detect-browser-language';
import useMyAxios from 'hooks/useMyAxios';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import useSWR from 'swr';
import MangerPaymentForUser from 'Pages/userManagement/MangerPaymentForUser';
import { PlanForAffliate } from 'Pages/public/landing/plans/PlanForAffliate';
import { UserMangementSettings } from 'Pages/userManagement/UserMangementSettings';
import { ReferralsSettings } from 'Pages/private/settings/Referrals';
import { AffiliateSettings } from 'Pages/userManagement/AffiliateSettings';
import { AffiliateOnly, ManageUsersOnly, isAffiliateUser } from 'Utils/settingsAccess';
import { CloudUpload } from 'Pages/cloudUpload';
import CreateUser from 'CreateNewUser/CreateUser';
import CreateUserV2 from 'CreateNewUser/v2/CreateUserV2';
import QuickUserUploadPage from 'Pages/userManagement/QuickUserUploadPage';
import ListingModalCopy from 'Components/ListingModalCopy';
import AddPlaylistModal from 'Components/AddPlaylistModal';
import CreateCategoryPage from 'Pages/private/content-layout/CreateCategory';
import AddSubCategoryModal from 'Components/AddSubCategoryModal';
import DropMe from 'Components/DropMe/DropMe';
import SuccessPopUp from 'Components/VideoPopup/SuccessPopUp';
// import QRCodeComponent from 'Utils/QRCodeComponent';
import { PhotoStorage } from 'Pages/private/storage/cloud/PhotoStorage';
import { CategoryCloud } from 'Pages/private/storage/cloud/CategoryCloud';
import { AlbumCloudStorage } from 'Pages/private/storage/cloud/AlbumCloudStorage';
import { GooglePhotosCloud } from 'Pages/private/storage/cloud/GooglePhotosCloud';
import {
  AccountSettingsV2,
  SettingsHomeV2,
  NotificationSettingsV2,
  CloudSettingsV2,
  BillingSettingsV2,
  ManagePaymentMethodsV2,
  ConnectedAccountSettingsV2,
  UserManagementSettingsV2,
  AffiliateSettingsV2,
  PrivacySettingsV2,
  TermsAndConditionSettingsV2,
  SubscriberAgreementSettingsV2,
  DashboardV2,
  ContentLayoutV2,
  StorageV2,
  YourVideosV2,
  ManageDeviceV2,
} from 'Pages/private/v2';
import { V2RouteSwitch } from 'Pages/private/v2/_shared/V2RouteSwitch';
import { AccessClassic } from 'Pages/private/access/AccessClassic';
import { ProfileClassic } from 'Pages/private/profile/ProfileClassic';
import { VideoDetailsClassic } from 'Pages/private/video-details/VideoDetailsClassic';
import { ContentLayoutDetailsClassic } from 'Pages/private/content-layout-details/ContentLayoutDetailsClassic';
import { InnerPageClassic } from 'Pages/private/innerpage/InnerPageClassic';
import { InviteEachUser } from 'Pages/public/authentication/Trail/InviteEachUser';
import { DeviceList } from 'Pages/public/authentication/Trail/DeviceList';
import { NewVerificationEmail } from 'Pages/public/authentication/newVerification-code/NewVerificationEmail';
import CardExp from 'Utils/CardExp';
import { isOtpVerificationTestException } from 'Utils/otpTestExceptions';
import { registerDevice, startDeviceHeartbeat, stopDeviceHeartbeat } from 'Utils/deviceSession';
import RotatingLoader from 'Components/RotatingLoader';
import { PlanToSwitch } from 'Pages/public/landing/plans/PlanToSwitch';
import { Avatar } from 'Pages/public/authentication/newSign-in/Avatar';
import { ChooseOccasion } from 'Pages/public/authentication/ChooseOccasion';
import { InterviewIntro } from 'Pages/public/interview/InterviewIntro';
import { InterviewHost } from 'Pages/public/interview/InterviewHost';
import { InterviewJoin } from 'Pages/public/interview/InterviewJoin';
import { InterviewReview } from 'Pages/public/interview/InterviewReview';
import VerifyUsingLink from 'Pages/public/authentication/newSign-in/VerifyUsingLink';
import AutoLogin from 'Pages/public/authentication/AutoLogin';
import TVLoginApprove from 'Pages/public/authentication/TVLoginApprove';
import AutoTVLogin from 'Pages/public/authentication/AutoTVLogin';
import ShortLink from 'Pages/public/ShortLink';
import MobileUpload from 'Pages/public/mobile-upload';
import ContributeMobileUpload from 'Pages/public/contribute-mobile-upload';
import { navigateTo } from './navigationHelper';
import { UploadModal } from './Components/upload-modal/index';
import UploadPanel from './Components/UploadPanel/UploadPanel';
import FloatingSidebar from './Components/FloatingSidebar';
import { UPLOAD_CONFIG } from './Config/uploadConfig';
import uploadManager from './Utils/uploadManagerSecure';
import SubscriberAgreementDarkMode from './Pages/public/SubscriberAgreementDarkMode';
import DownArrow from './Assets/arrowdown.png';
import UpArrow from './Assets/arrowup.png';
import styles from './app.module.css';

// Access & Profile were rebuilt for the new UI (no classic fallback in-route),
// so switch them like the other v2 routes: classic pages come from the ported
// MemberShipHotFixesLive components.
const AccessSwitch = () => <V2RouteSwitch classic={<AccessClassic />} modern={<Access />} />;
const ProfileSwitch = () => <V2RouteSwitch classic={<ProfileClassic />} modern={<Profile />} />;
// Video & category detail pages were rebuilt for the new UI in place; keep the
// classic (legacy production) versions for the old UI.
const VideoDetailsSwitch = () => <V2RouteSwitch classic={<VideoDetailsClassic />} modern={<VideoDetails />} />;
const ContentLayoutDetailsSwitch = () => (
  <V2RouteSwitch
    classic={<ContentLayoutDetailsClassic showDownloadAll />}
    modern={<ContentLayoutDetails showDownloadAll />}
  />
);
const InnerPageSwitch = () => <V2RouteSwitch classic={<InnerPageClassic />} modern={<InnerPage />} />;

const CircularProgress = withStyles({
  circle: {
    color: '#fff !important',
  },
})(MuiCircularProgress);

// Remember where a logged-out visitor was headed so the sign-in flow can bring
// them back afterwards (read in newSign-in onSuccess via localStorage
// `storedPath`). localStorage is per-origin, so it survives the marketing-site
// detour and is still here when they return to sign in.
//
// Skips, in order: a live session, the root/landing, sign-in/up screens, all
// auth / token-handoff / verification flows (these carry their own tokens/keys —
// remembering e.g. /autoLogin?token=… would make a later login replay a stale
// token), and public/marketing/legal pages. Only genuine restricted in-app
// destinations are kept.
const rememberDeepLinkPath = () => {
  try {
    if (typeof window === 'undefined') return;
    if (localStorage.getItem('token')) return; // logged in — nothing to remember
    const { pathname, search } = window.location;
    if (!pathname || pathname === '/') return; // root / marketing landing
    if (/sign-?(in|up)/i.test(pathname)) return; // sign-in / sign-up
    // Auth / token-handoff / verification flows (autoLogin, TV login, auto-verify,
    // verify-*, email-verification, forgot/set password, mobile verification,
    // mobile-camera session, /shrt short links).
    if (/^\/(auto-?login|auto-?tv-?login|tv-?login|auto-?verif|verif|new-?verif|email-verif|forgot|set-?password|reset|mobile-?verification|new-?mobile|mobile-camera|shrt)/i.test(pathname)) return;
    // Public / marketing / legal / tokenless-public routes in the signed-out tree.
    if (/^\/(home|plans|privacy|terms|subscriber|choose|referral|gift|receiving-gift|share|contribute|cloud-upload|mobile-upload|interview)/i.test(pathname)) return;
    localStorage.setItem('storedPath', `${pathname}${search}`);
  } catch (e) { /* storage blocked — nothing to remember */ }
};

// Run once at module load — BEFORE React renders, auto-signin runs, or any
// redirect effect fires — so the capture is reliable even when the visitor
// arrives with a stale/expired token (auto-signin then clears it and bounces to
// the marketing site) or when the ExternalRedirect effect would be preempted.
rememberDeepLinkPath();

// Redirect to external landing page for logged out users
const ExternalRedirect = ({ to, rememberPath = false }) => {
  useEffect(() => {
    // Check if user has token - if so, don't redirect (they just logged in)
    const token = localStorage.getItem('token');
    if (!token) {
      // Also capture here for in-app (SPA) navigations to a restricted path,
      // where the module-load capture above didn't run. Same guarded rules.
      if (rememberPath) {
        rememberDeepLinkPath();
      }
      window.location.href = to;
    }
  }, [to, rememberPath]);
  return null;
};

const defaultAppSnackbar = {
  isVisible: false,
  type: undefined,
  message: '',
  duration: 1500,
};
const appSnackbarAutoHideDuration = 1500;
const getToken = () => localStorage.getItem('token');
// localStorage stringifies, so an absent value lands as the literal "undefined"
// / "null" — a truthy token the app then sends to the API, which answers 401 and
// trips the logout-and-reload path in the getMyProfile onError below.
const isUsableToken = (token) => !!token && token !== 'undefined' && token !== 'null';
const setToken = (token) => {
  // Never let a missing token overwrite a working session.
  if (!isUsableToken(token)) return false;
  localStorage.setItem('token', token);
  return true;
};
// Routes an unverified user is allowed to sit on — the verification screens
// themselves, plus the flows that must keep working while verification is
// outstanding (password reset, the auto-login hand-offs, the email-link
// landings). Everything else redirects them back to the OTP screen.
const verificationExemptPaths = [
  Pathname.authentication.newverifyCode,
  Pathname.authentication.newverifyCodeEmail,
  Pathname.authentication.newmobileVerification,
  Pathname.authentication.mobileVerification,
  Pathname.authentication.verifyCode,
  Pathname.authentication.verifyEmail,
  Pathname.authentication.verifyEmailLink,
  Pathname.authentication.verifyEmailFallback,
  Pathname.authentication.forgotPassword,
  Pathname.authentication.setPassword,
  Pathname.authentication.autoLogin,
  Pathname.autoTVLogin,
  Pathname.tvLoginApprove,
];
// The gift-recipient lane (/setpassword → /gift-content → /gift-payment) is a
// separate onboarding flow that never asks for a mobile number, so the OTP gate
// below must leave it alone. Setting a password there was tripping
// `passwordIsSet` and bouncing recipients onto the verification screen the
// moment they landed on /gift-content. Matched by route rather than by the
// `giftUid` localStorage marker, which is written once and never cleared —
// keying off that would silently disable the gate for the whole browser.
const giftFlowPathPrefixes = ['/gift', '/receiving-gift'];
const removeToken = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('inviteUser');
  localStorage.removeItem('dismissedPaymentDialog');
  // Drop the manager-impersonation markers on every logout path. Without
  // this, exiting via the regular Log Out button (instead of the banner's
  // Exit) leaves isAccessingUser=true behind — a subsequent login on the
  // same browser could then falsely trip the "Accessed X's account" banner.
  localStorage.removeItem('isAccessingUser');
  localStorage.removeItem('accessedUserId');
  localStorage.removeItem('managerOwnToken');
  // The new-UI gate keys off this email, so leaving it behind kept the new
  // layout on the signed-out login page — and would have handed it to whoever
  // signed in next. The per-account preference (newUIEnabled:<email>) is kept,
  // so an allow-listed user gets their choice back when they return.
  localStorage.removeItem('userEmail');
  clearUiLane();
  // Forget that the getting-started banner was closed / collapsed, so the next
  // login reminds the user about whatever they still haven't explored.
  clearOnboardingBannerState();
};
const setEmailVerified = (isVerified) => {
  localStorage.setItem('isVerified', isVerified);
};
const getEmailVerified = () => localStorage.getItem('isVerified');
const removeEmailVerified = () => {
  localStorage.removeItem('isVerified');
};
/**
 * Parks any signed-in account that has NO payment method on
 * /quick-user-payment and keeps it there until a card is stored. Back button
 * included — every location change re-runs this, and the redirect is `replace`
 * so there's no entry to go back to.
 *
 * Exempt:
 *   • Lifetime plans — paid once, forever; there's nothing left to bill.
 *   • Onboarding / auth / gift / public routes, so a new user can still walk
 *     setpassword → avatar → interview intro before being asked, and a gift
 *     recipient can still open their gift.
 *   • A manager impersonating a user — the manager is inside someone else's
 *     account to work in it, not to be billed for it.
 *
 * This is the ENFORCEMENT layer only — it catches the back button, a typed URL
 * and a mid-session plan change. The login screens make the same call
 * themselves (see needsPaymentMethod) and route straight here, so a normal
 * login never waits on this component and never shows an extra loader.
 */
const PaymentMethodGate = ({ userIsSignedIn, isInActiveUser, children }) => {
  const gateLocation = useLocation();
  const gateNavigate = useNavigate();
  const {
    subscriptionDetails,
    allPlans,
    mangerToken,
    userAccessedData,
    getAccessChecker,
    userDetails,
  } = useContext(AppContext);

  // "Is a manager driving this session RIGHT NOW" — deliberately NOT
  // getAccessChecker/success, which stays true on the managed user's own login
  // too. Only the in-session React state and the isAccessingUser marker — both
  // written by handleClick/switchingFunction on the way in, and cleared on
  // sign-in, sign-up, autologin, logout and Exit — mean that.
  const impersonating = !!mangerToken
    || !!userAccessedData
    || localStorage.getItem('isAccessingUser') === 'true';

  // "Is this account's bill someone else's problem" — a different question, and
  // the one that decides whether a missing card is a PROBLEM at all. See
  // ownsItsOwnBilling: manager-managed users and the manager accounts
  // themselves are paid for elsewhere and hold no card by design, so a gate
  // built on "paid plan + no card" must let them straight through. The affiliate
  // allow-list is checked as well, so a brand-new affiliate account is covered
  // before it has any access rows for /getUserAccess to report.
  const billedElsewhere = !ownsItsOwnBilling(getAccessChecker)
    || isAffiliateUser(userDetails?.email);

  // Inactive/expired accounts render a reduced route table that has no
  // /quick-user-payment entry — redirecting them there would bounce off the
  // catch-all straight back to /dashboard, forever. They're already funnelled
  // through the expired-plan flow, so leave them to it.
  const needsCard = userIsSignedIn
    && !isInActiveUser
    && !impersonating
    && !billedElsewhere
    // Never lock anyone out on a subscription record fetched for a DIFFERENT
    // token. Exiting an impersonated account clears the markers above ~3 awaits
    // before the manager's own record lands, and without this the manager gets
    // parked on the accessed user's missing card in the meantime.
    && isSubscriptionForCurrentAccount(subscriptionDetails)
    && !isPaymentGateExemptPath(gateLocation.pathname, gateLocation.search)
    && needsPaymentMethod(subscriptionDetails, allPlans);

  useEffect(() => {
    if (!needsCard) return;
    gateNavigate(Pathname.authentication.quickUserPayment, { replace: true });
  }, [needsCard, gateLocation.pathname, gateLocation.search]);

  // Don't paint the page we're already leaving. This is one frame — the effect
  // above redirects in the same commit — so there's no loader and no wait.
  if (needsCard) return null;

  return <>{children}</>;
};

const RouteSwitch = ({
  isNewUser,
  userIsSignedIn,
  isInActiveUser,
  isEmailAllowed,
}) => {
  const currentLocation = useLocation();
  const routeNavigate = useNavigate();
  const [routeSearchParams] = useSearchParams();

  // Persist ?fromGift=true across all navigations once set from /profile?fromGift=true
  useEffect(() => {
    const hasFromGiftInUrl = routeSearchParams.get('fromGift') === 'true';
    const hasFromGiftInSession = sessionStorage.getItem('fromGift') === 'true';

    if (hasFromGiftInUrl && !hasFromGiftInSession) {
      sessionStorage.setItem('fromGift', 'true');
    }

    if (!hasFromGiftInUrl && hasFromGiftInSession) {
      const newParams = new URLSearchParams(currentLocation.search);
      newParams.set('fromGift', 'true');
      routeNavigate(`${currentLocation.pathname}?${newParams.toString()}`, { replace: true });
    }
  }, [currentLocation.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentLocation.pathname]);

  // Refresh the new-UI allow-list once per app load. The gate reads a cached
  // copy synchronously, so this only keeps that cache current — a failure is
  // silent and leaves the previous list in place.
  useEffect(() => {
    refreshAllowedEmails();
  }, []);
  useEffect(() => {
    if (window.gtag) {
      // Set user property (you’ll see this in DebugView if enabled)
      window.gtag('set', {
        user_properties: {
          dev_user: 'dev testing in prod',
        },
      });
      console.log('GA page view for:', currentLocation.pathname);
      // Send page view with route info
      window.gtag('config', 'G-8QBGJ3N2K3', {
        page_path: currentLocation.pathname,
      });
    }
  }, [currentLocation]);
  return (
    <PaymentMethodGate {...{ userIsSignedIn, isInActiveUser }}>
      {/* Single persistent floating rail across all app pages — sits above
          <Routes> so it never remounts on navigation (no glitch). */}
      {userIsSignedIn && <FloatingSidebar />}
      <Routes>
      {/* Auto-login must run regardless of any existing session. It clears the
          old token and signs in with the token from the URL. Previously this
          route lived only in the signed-out branch below, so an already
          logged-in user hitting /autoLogin fell through to that branch's `*`
          catch-all and was bounced to /access as the OLD user — the AutoLogin
          component never mounted and the new token was never processed. Mounted
          here, above the auth ternary, so the explicit path always beats the
          per-branch catch-alls whatever userIsSignedIn is. */}
      <Route
        path={Pathname.authentication.autoLogin}
        element={<AutoLogin />}
      />
      {userIsSignedIn ? (
        isInActiveUser ? (
          <>
            <Route path={Pathname.access} element={<AccessSwitch />} />
            <Route
              path={Pathname.dashboard}
              element={(
                <V2RouteSwitch
                  classic={<Dashboard />}
                  modern={<DashboardV2 />}
                />
              )}
            />
            <Route
              path={Pathname.authentication.ecosystem}
              element={<DeviceList />}
            />
            {/* <Route path={Pathname.storage.basePath}>
                <Route index element={<Storage />} />
              </Route> */}
            <Route
              path={Pathname.settings.basePath}
              element={(
                <V2RouteSwitch
                  classic={<BillingSettings />}
                  modern={<BillingSettingsV2 />}
                />
              )}
            >
              <Route
                index
                element={(
                  <V2RouteSwitch
                    classic={<BillingSettings />}
                    modern={<BillingSettingsV2 />}
                  />
                )}
              />
              {/* <Route path="manage-payment-methods" element={<ManagePaymentMethods />} /> */}
              <Route
                path="privacy"
                element={(
                  <V2RouteSwitch
                    classic={<PrivacySettings />}
                    modern={<PrivacySettingsV2 />}
                  />
                )}
              />
              <Route
                path="billings"
                element={(
                  <V2RouteSwitch
                    classic={<BillingSettings />}
                    modern={<BillingSettingsV2 />}
                  />
                )}
              />
              <Route
                path="termsAndConditions"
                element={(
                  <V2RouteSwitch
                    classic={<TermsAndConditionSettings />}
                    modern={<TermsAndConditionSettingsV2 />}
                  />
                )}
              />
              <Route
                path="subscriberAgreement"
                element={(
                  <V2RouteSwitch
                    classic={<SubscriberAgreementSettings />}
                    modern={<SubscriberAgreementSettingsV2 />}
                  />
                )}
              />
            </Route>
            <Route path={Pathname.settings.basePath}>
              <Route
                index
                element={(
                  <V2RouteSwitch
                    classic={<AccountSettings />}
                    modern={<AccountSettingsV2 />}
                  />
                )}
              />
              <Route
                path="manage-payment-methods"
                element={(
                  <V2RouteSwitch
                    classic={<ManagePaymentMethods />}
                    modern={<ManagePaymentMethodsV2 />}
                  />
                )}
              />
              <Route
                path="billings-and-payments"
                element={(
                  <V2RouteSwitch
                    classic={<BillingSettings />}
                    modern={<BillingSettingsV2 />}
                  />
                )}
              />
            </Route>
            <Route path={Pathname.profile.basePath} element={<ProfileSwitch />}>
              <Route index element={<ProfileSwitch />} />
              <Route path=":profileID" element={<ProfileSwitch />} />
            </Route>
            <Route path={Pathname.watchlist.basePath} element={<Watchlist />}>
              <Route index element={<Watchlist />} />
              <Route path=":profileID" element={<Watchlist />} />
            </Route>
            <Route path={Pathname.search.basePath} element={<SearchPage />}>
              <Route index element={<SearchPage />} />
              <Route path=":profileID" element={<SearchPage />} />
            </Route>
            <Route path={Pathname.video} element={<VideoDetailsSwitch />} />
            <Route path={Pathname.album} element={<AlbumDetails />} />
            <Route path={Pathname.player} element={<VideoPlayer />} />
            <Route path={Pathname.playerFormat} element={<VideoPlayer />} />
            <Route path={Pathname.albumPlayer} element={<AlbumPlayer />} />
            <Route
              path={Pathname.authentication.setPassword}
              element={<SetPassword />}
            />
            <Route path={Pathname.innerpage} element={<InnerPageSwitch />} />
            <Route path={Pathname.home} element={<AccessSwitch />} />
            <Route
              path={Pathname.plans}
              element={<PublicPlans userIsSignedIn={userIsSignedIn} />}
            />
            <Route
              path={Pathname.purchasePlans}
              element={<PublicPlans userIsSignedIn={userIsSignedIn} />}
            />
            <Route path={Pathname.viewerInvite} element={<ViewerInvite />} />
            <Route path={Pathname.share} element={<SharedItem />} />
            <Route path="/" element={<Navigate to={Pathname.access} replace />} />
            <Route path="*" element={<Navigate to={Pathname.dashboard} />} />
          </>
        ) : (
          <>
            <Route path={Pathname.home} element={<AccessSwitch />} />
            <Route
              path={Pathname.authentication.toTrail}
              element={<Welcome />}
            />
            <Route
              path={Pathname.authentication.quickUserWelcome}
              element={<QuickUserWelcome />}
            />
            <Route
              path={Pathname.authentication.quickUserPayment}
              element={<QuickUserPayment />}
            />
            <Route
              path={Pathname.authentication.avatar}
              element={<Avatar />}
            />
            <Route
              path={Pathname.authentication.chooseOccasion}
              element={<ChooseOccasion />}
            />
            <Route
              path={Pathname.interview.intro}
              element={<InterviewIntro />}
            />
            <Route
              path={Pathname.interview.host}
              element={<InterviewHost />}
            />
            <Route
              path={Pathname.interview.join}
              element={<InterviewJoin />}
            />
            <Route
              path={Pathname.interview.review}
              element={<InterviewReview />}
            />
            <Route
              path={Pathname.authentication.invite}
              element={<InviteEachUser />}
            />
            <Route
              path={Pathname.authentication.ecosystem}
              element={<DeviceList />}
            />
            <Route
              path={Pathname.gettingStarted}
              element={<GettingStarted />}
            />
            <Route path={Pathname.access} element={<AccessSwitch />} />
            <Route path={Pathname.innerpage} element={<InnerPageSwitch />} />
            <Route
              path={Pathname.dashboard}
              element={(
                <V2RouteSwitch
                  classic={<Dashboard />}
                  modern={<DashboardV2 />}
                />
              )}
            />
            <Route path={Pathname.storage.basePath}>
              <Route
                path="/storage/videos"
                element={(
                  <V2RouteSwitch
                    classic={<Storage />}
                    modern={<StorageV2 />}
                  />
                )}
              />
              <Route
                path="/storage/videos/:category"
                element={<CategoryCloud />}
              />
              <Route
                path="/storage/photos/:albumid"
                element={<AlbumCloudStorage />}
              />
              <Route path="/storage/photos" element={<PhotoStorage />} />
              <Route
                path="/storage/google-photos"
                element={<GooglePhotosCloud />}
              />
            </Route>
            <Route
              path={Pathname.yourVideos}
              element={(
                <V2RouteSwitch
                  classic={<YourVideos />}
                  modern={<YourVideosV2 />}
                />
              )}
            />
            <Route
              path={Pathname.contentLayout}
              element={(
                <V2RouteSwitch
                  classic={<ContentLayout />}
                  modern={<ContentLayoutV2 />}
                />
              )}
            />
            <Route
              path={Pathname.contentLayoutDashboard}
              element={<ContentLayoutDashboard />}
            />
            <Route
              path={Pathname.contentYourVideosDashboard}
              element={<ContentLayoutDashboard />}
            />
            <Route
              path={Pathname.contentLayoutType}
              element={<ContentLayoutDetailsSwitch />}
            />
            <Route
              path={Pathname.tvLogin}
              element={(
                <V2RouteSwitch
                  classic={<ManageDevice />}
                  modern={<ManageDeviceV2 />}
                />
              )}
            />
            <Route path={Pathname.tvLoginApprove} element={<TVLoginApprove />} />
            <Route path={Pathname.autoTVLogin} element={<AutoTVLogin />} />
            <Route
              path={Pathname.authentication.mobileVerification}
              element={<MobileVerification />}
            />
            <Route
              path={Pathname.authentication.newmobileVerification}
              element={<NewMobileVerification />}
            />
            <Route
              path={Pathname.authentication.verifyCode}
              element={<VerifyCode />}
            />
            {/* Reachable while signed in: the post-login verification gate
                holds a user with neither email nor mobile verified here, and by
                then the token is set so the signed-out tree no longer applies. */}
            <Route
              path={Pathname.authentication.newverifyCode}
              element={<NewVerifyCode />}
            />
            {/* Layout route: <ViewersLayoutSwitch> holds the shared chrome —
                including <Page>, and therefore the app header — mounted across
                the four tabs, so switching tabs swaps only the tab content. */}
            <Route path={Pathname.viewers.basePath} element={<ViewersLayoutSwitch />}>
              <Route index element={<Viewers />} />
              <Route path="viewerlists" element={<Viewers />} />
              <Route path="groups" element={<Groups />} />
              <Route path="successors" element={<Successors />} />
              <Route path="connected-accounts" element={<ConnectedAccounts />} />
            </Route>
            <Route path={Pathname.settings.basePath}>
              <Route
                index
                element={(
                  <V2RouteSwitch
                    classic={<AccountSettings />}
                    modern={<SettingsHomeV2 />}
                  />
                )}
              />
              <Route
                path="accounts"
                element={(
                  <V2RouteSwitch
                    classic={<AccountSettings />}
                    modern={<AccountSettingsV2 />}
                  />
                )}
              />
              <Route
                path="notifications"
                element={(
                  <V2RouteSwitch
                    classic={<NotificationSettings />}
                    modern={<NotificationSettingsV2 />}
                  />
                )}
              />
              <Route
                path="manage-payment-methods"
                element={(
                  <V2RouteSwitch
                    classic={<ManagePaymentMethods />}
                    modern={<ManagePaymentMethodsV2 />}
                  />
                )}
              />
              <Route
                path="manageUsers"
                element={(
                  <V2RouteSwitch
                    classic={<ConnectedAccountSettings />}
                    modern={<ConnectedAccountSettingsV2 />}
                  />
                )}
              />
              <Route
                path="cloud"
                element={(
                  <V2RouteSwitch
                    classic={<CloudSettings />}
                    modern={<CloudSettingsV2 />}
                  />
                )}
              />
              <Route
                path="billings-and-payments"
                element={(
                  <V2RouteSwitch
                    classic={<BillingSettings />}
                    modern={<BillingSettingsV2 />}
                  />
                )}
              />
              <Route
                path="terms-of-use"
                element={(
                  <V2RouteSwitch
                    classic={<TermsAndConditionSettings />}
                    modern={<TermsAndConditionSettingsV2 />}
                  />
                )}
              />
              <Route
                path="subscriber-agreement"
                element={(
                  <V2RouteSwitch
                    classic={<SubscriberAgreementSettings />}
                    modern={<SubscriberAgreementSettingsV2 />}
                  />
                )}
              />
              <Route
                path="privacy"
                element={(
                  <V2RouteSwitch
                    classic={<PrivacySettings />}
                    modern={<PrivacySettingsV2 />}
                  />
                )}
              />
              <Route
                path="manage-users"
                element={(
                  <ManageUsersOnly>
                    <V2RouteSwitch
                      classic={<UserMangementSettings />}
                      modern={<UserManagementSettingsV2 />}
                    />
                  </ManageUsersOnly>
                )}
              />
              <Route path="referrals" element={<ReferralsSettings />} />
              <Route
                path="affiliate"
                element={(
                  <AffiliateOnly>
                    <V2RouteSwitch
                      classic={<AffiliateSettings />}
                      modern={<AffiliateSettingsV2 />}
                    />
                  </AffiliateOnly>
                )}
              />
            </Route>
            <Route path={Pathname.profile.basePath} element={<ProfileSwitch />}>
              <Route index element={<ProfileSwitch />} />
              <Route path=":profileID" element={<ProfileSwitch />} />
            </Route>
            <Route path={Pathname.watchlist.basePath} element={<Watchlist />}>
              <Route index element={<Watchlist />} />
              <Route path=":profileID" element={<Watchlist />} />
            </Route>

            <Route path={Pathname.search.basePath} element={<SearchPage />}>
              <Route index element={<SearchPage />} />
              <Route path=":profileID" element={<SearchPage />} />
            </Route>
            <Route>
              <Route path="create-user" element={<CreateUser />} />
              <Route path="create-user-v2" element={<CreateUserV2 />} />
            </Route>
            <Route>
              <Route path="quick-user-upload" element={<QuickUserUploadPage />} />
            </Route>
            <Route>
              <Route path="cloud-upload" element={<CloudUpload />} />
            </Route>
            <Route>
              <Route path="pay-by-manger" element={<MangerPaymentForUser />} />
            </Route>
            <Route>
              <Route path="affiliate" element={<PlanForAffliate />} />
            </Route>
            <Route>
              <Route path="switchplan" element={<PlanToSwitch />} />
            </Route>
            {/* /referral, /referral/:code and /choose are public-only. When
                signed in they intentionally fall through to the catch-all
                below (→ /access), same as /newSign-in. */}
            <Route path="/home" element={<ProjectorStreamHome />} />
            <Route path="/home/:code" element={<ProjectorStreamHome />} />
            <Route path="/gift2" element={<ProjectorStreamHome src="/gift-clone.html" title="Projector Gift" />} />
            <Route path="/gift2/:code" element={<ProjectorStreamHome src="/gift-clone.html" title="Projector Gift" />} />
            <Route path={Pathname.video} element={<VideoDetailsSwitch />} />
            <Route path={Pathname.album} element={<AlbumDetails />} />
            <Route path={Pathname.player} element={<VideoPlayer />} />
            <Route path={Pathname.playerFormat} element={<VideoPlayer />} />
            <Route path={Pathname.albumPlayer} element={<AlbumPlayer />} />
            <Route
              path={Pathname.authentication.setPassword}
              element={<SetPassword />}
            />
            <Route
              path={Pathname.plans}
              element={<PublicPlans userIsSignedIn={userIsSignedIn} />}
            />
            <Route
              path={Pathname.purchasePlans}
              element={<PublicPlans userIsSignedIn={userIsSignedIn} />}
            />
            <Route
              path={Pathname.projectorTermsAndServices}
              element={<TermsAndServices />}
            />
            <Route path={Pathname.shortLink} element={<ShortLink />} />
            <Route path={Pathname.mobileUpload} element={<MobileUpload />} />
            <Route path={Pathname.contributeMobileUpload} element={<ContributeMobileUpload />} />
            <Route
              path={Pathname.giftFlow.intro}
              element={<GiftFlowIntro />}
            />
            <Route
              path={Pathname.giftFlow.receiving}
              element={<ReceivingGift />}
            />
            <Route
              path={Pathname.giftFlow.payment}
              element={<GiftPayment />}
            />
            <Route
              path={Pathname.giftFlow.content}
              element={<GiftContent />}
            />
            <Route
              path={Pathname.contribute}
              element={<ContributePage />}
            />
            <Route path="cloud-upload" element={<CloudUpload />} />
            <Route path={Pathname.viewerInvite} element={<ViewerInvite />} />
            <Route path={Pathname.share} element={<SharedItem />} />
            <Route path="/" element={<Navigate to={Pathname.access} replace />} />
            <Route path="*" element={<Navigate to={Pathname.access} />} />
          </>
        )
      ) : (
        <>
          <Route path={Pathname.home} element={<ExternalRedirect to="https://www.projectorstream.com/" />} />
          {/* ORIGINAL - Commented out for WordPress redirect:
          <Route
            path={Pathname.plans}
            element={<PublicPlans userIsSignedIn={userIsSignedIn} />}
          />
          */}
          <Route
            path={Pathname.plans}
            element={<ExternalRedirect to="https://www.projectorstream.com/" />}
          />
          <Route>
            <Route path="/referral/:code" element={<Referral />} />
            {/* No code → send to /choose, which renders the same chooser with
                param-free Yourself/gift links. */}
            <Route path="/referral" element={<Navigate to={Pathname.choose} replace />} />
            <Route path="/choose" element={<Referral />} />
          </Route>
          <Route path="/home" element={<ProjectorStreamHome />} />
          <Route path="/home/:code" element={<ProjectorStreamHome />} />
          <Route path="/gift2" element={<ProjectorStreamHome src="/gift-clone.html" title="Projector Gift" />} />
          <Route path="/gift2/:code" element={<ProjectorStreamHome src="/gift-clone.html" title="Projector Gift" />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms-of-use" element={<TermsAndConditions />} />
          <Route
            path="/subscriberAgreement"
            element={<SubscriberAgreementDarkMode />}
          />
          <Route path={Pathname.settings.basePath}>
            {/* /settings itself is NOT a public page. It used to render the
                privacy policy here, which made logging out from /settings —
                exactly where the "Use classic layout" switch leaves you —
                flash the privacy screen for the frame(s) between the session
                being cleared and the redirect to sign-in. The legal pages stay
                public on their own paths below (the footers link to
                /settings/privacy with target=_blank). */}
            <Route
              index
              element={(
                <Navigate to={Pathname.authentication.newsignIn} replace />
              )}
            />
            <Route
              path={`${Pathname.settings.basePath}/privacy`}
              element={<Privacy />}
            />
            <Route
              path={`${Pathname.settings.basePath}/terms-of-use`}
              element={<TermsAndConditions />}
            />
            <Route
              path={`${Pathname.settings.basePath}/subscriber-agreement`}
              element={<SubscriberAgreementDarkMode />}
            />
            {/* Every other settings page needs a session. Send them to sign-in
                rather than letting them fall through to the `*` catch-all,
                which bounces the visitor off to the marketing site. */}
            <Route
              path="*"
              element={(
                <Navigate to={Pathname.authentication.newsignIn} replace />
              )}
            />
          </Route>
          {/* <Route path={Pathname.authentication.signUp} element={<SignUp />} /> */}
          <Route
            path={Pathname.authentication.signUp_refferedBy}
            element={<SignUp />}
          />
          <Route path={Pathname.authentication.signIn} element={<NewSignIn />} />
          <Route
            path={Pathname.authentication.newSignUp}
            element={<NewSignIn />}
          />
          <Route
            path={Pathname.authentication.newsignIn}
            element={<NewSignIn />}
          />
          <Route path="/sign-in" element={<Navigate to="/newSign-in" replace />} />
          <Route
            path={Pathname.authentication.forgotPassword}
            element={<ForgotPassword />}
          />
          <Route
            path={Pathname.authentication.setPassword}
            element={<SetPassword />}
          />
          <Route
            path={Pathname.mobileCamera}
            element={<MobileCameraPage />}
          />
          <Route
            path={Pathname.authentication.verifyEmail}
            element={<EmailVerification />}
          />
          <Route
            path={Pathname.authentication.verifyEmailLink}
            element={<EmailVerificationLink />}
          />
          <Route
            path={Pathname.authentication.verifyEmailFallback}
            element={<VerifyUsingLink />}
          />
          {/* /autoLogin is mounted once above the auth ternary so it works for
              both signed-in and signed-out visitors — see the note there. */}
          <Route
            path={Pathname.autoTVLogin}
            element={<AutoTVLogin />}
          />
          {/* TV login while signed out: both the code link (/tv-login?code=…)
              and the consent screen itself park the target path in
              `storedPath` and hand off to sign-in, which navigates back here
              once there's a session — so the Allow/Decline prompt is always
              what the user actually sees. */}
          <Route
            path={Pathname.tvLoginApprove}
            element={<TVLoginApprove />}
          />
          <Route
            path={Pathname.tvLogin}
            element={<TVLoginApprove />}
          />
          <Route
            path={Pathname.authentication.mobileVerification}
            element={<MobileVerification />}
          />
          <Route
            path={Pathname.authentication.newmobileVerification}
            element={<NewMobileVerification />}
          />
          <Route
            path={Pathname.authentication.verifyUsingLink}
            element={<VerifyUsingLink />}
          />
          <Route
            path={Pathname.authentication.newverifyCode}
            element={<NewVerifyCode />}
          />
          <Route
            path={Pathname.authentication.newverifyCodeEmail}
            element={<NewVerificationEmail />}
          />
          <Route
            path={Pathname.giftFlow.intro}
            element={<GiftFlowIntro />}
          />
          <Route
            path={Pathname.giftFlow.receiving}
            element={<ReceivingGift />}
          />
          <Route
            path={Pathname.contribute}
            element={<ContributePage />}
          />
          <Route path="cloud-upload" element={<CloudUpload />} />
          <Route path={Pathname.shortLink} element={<ShortLink />} />
          <Route path={Pathname.mobileUpload} element={<MobileUpload />} />
          {/* Interview guest landing is intentionally PUBLIC. The host
              shares a link to anyone — invitees may not have an account
              and shouldn't be bounced to login. The room UUID is the
              only credential needed (tokenless by design — see
              src/Api/roomsApi.js). The other interview routes (intro,
              host, review) stay signed-in-only above. */}
          <Route
            path={Pathname.interview.join}
            element={<InterviewJoin />}
          />
          <Route path={Pathname.contributeMobileUpload} element={<ContributeMobileUpload />} />
          <Route path={Pathname.viewerInvite} element={<ViewerInvite />} />
          <Route path={Pathname.share} element={<SharedItem />} />
          <Route
            path="*"
            element={<ExternalRedirect to="https://www.projectorstream.com/" rememberPath />}
          />
        </>
      )}
      </Routes>
    </PaymentMethodGate>
  );
};

export const App = () => {
  // Boot fingerprint: a fresh timestamp here on every print means the App tree
  // remounted (hard reload). Same timestamp across logs means SPA navigation.
  // eslint-disable-next-line no-console
  // console.log('[SWITCH-DEBUG] BOOT. App() render. bootId=', window.spBootId || (window.spBootId = Date.now()), ' path=', window.location.pathname);
  const firebaseConfig = {
    apiKey: firebaseCredentials?.apiKey,
    authDomain: firebaseCredentials?.authDomain,
    databaseURL: firebaseCredentials?.databaseURL,
    projectId: firebaseCredentials?.projectId,
    storageBucket: firebaseCredentials?.storageBucket,
    messagingSenderId: firebaseCredentials?.messagingSenderId,
    appId: firebaseCredentials?.appId,
    measurementId: firebaseCredentials?.measurementId,
  };
  const app = initializeApp(firebaseConfig);
  const database = getDatabase(app);
  const dispatch = useDispatch();
  const [firebaseDatabase, setFirebaseDatabase] = useState(database);
  const databaseRefVer = ref(database, 'version');
  const databaseRef = ref(firebaseDatabase, 'status_from_sns');
  const [data, setData] = useState([]);
  useEffect(() => {
    const dataCallback = (snapshot) => {
      if (snapshot.exists()) {
        const dataObj = snapshot.val();
        setData(dataObj);
      }
    };
    onValue(databaseRef, dataCallback);
    return () => {
      off(databaseRef, 'value', dataCallback);
    };
  }, []);

  useEffect(() => {
    const dataCallback = (snapshot) => {
      if (snapshot.exists()) {
        const dataObj = snapshot.val();
        if (!localStorage.getItem('version')) {
          localStorage.setItem('version', dataObj);
        } else if (localStorage.getItem('version') !== dataObj.toString()) {
          // Clear cookies
          const cookies = document.cookie.split('; ');
          cookies.forEach((cookie) => {
            const [name] = cookie.split('=');
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
          });
          // Clear local storage
          localStorage.clear();
          // Clear session storage
          sessionStorage.clear();
          // Clear cache (for some browsers)
          if (window.caches) {
            window.caches.keys().then((cacheNames) => {
              cacheNames.forEach((cacheName) => {
                window.caches.delete(cacheName);
              });
            });
          }
          localStorage.setItem('version', dataObj);
          window.location.reload(true);
        }
      }
    };
    onValue(databaseRefVer, dataCallback);
    return () => {
      off(databaseRefVer, 'value', dataCallback);
    };
  }, []);
  const databaseRefMinor = ref(database, 'versionMinor');
  useEffect(() => {
    const dataCallbackMinor = (snapshot) => {
      if (snapshot.exists()) {
        const dataObj = snapshot.val();
        if (!localStorage.getItem('versionMinor')) {
          localStorage.setItem('versionMinor', dataObj);
        } else if (
          localStorage.getItem('versionMinor') !== dataObj.toString()
        ) {
          localStorage.setItem('versionMinor', dataObj);
          window.location.reload(true);
        }
      }
    };
    onValue(databaseRefMinor, dataCallbackMinor);
    return () => {
      off(databaseRefMinor, 'value', dataCallbackMinor);
    };
  }, []);
  const [userIsSignedIn, setUserSignInStatus] = useState(false);
  // Device/session tracking (best-effort, never blocks any flow). Enrolls the
  // browser on cold-start for already-logged-in users (fires once autoSignIn
  // flips userIsSignedIn), and drives the heartbeat: start while signed in,
  // stop on logout. Explicit register also runs at login time in the sign-in
  // screens right after setToken().
  useEffect(() => {
    try {
      if (userIsSignedIn && getToken()) {
        registerDevice();
        startDeviceHeartbeat();
      } else {
        stopDeviceHeartbeat();
      }
    } catch (e) {
      // best-effort: ignore
    }
    return () => { stopDeviceHeartbeat(); };
  }, [userIsSignedIn]);
  const [removeProjectorTextChange, setRemoveProjectorTextChange] = useState(false);
  const [isS3UploadComplete, setS3UploadComplete] = useState({});
  let totalElapsedTime = 0;
  let totalElapsedSize = 0;
  let totalRemainingSize = 0;
  const [eta, setEta] = useState();
  const uploadDetails = useSelector((state) => state.uploadDetails);
  useEffect(() => {
    const values = Object.values(uploadDetails);
    totalElapsedTime = values.reduce((acc, item) => acc + item.elapsed_time, 0);
    totalElapsedSize = values.reduce((acc, item) => acc + item.elapsed_size, 0);
    totalRemainingSize = values.reduce(
      (acc, item) => acc + item.remaining_size,
      0
    );
    setEta(
      (
        (totalRemainingSize / (totalElapsedSize / totalElapsedTime) + 1)
        / 60
      )?.toFixed(0)
    );
  }, [uploadDetails, eta]);
  const totalOnGoingUploadSize = Object.values(uploadDetails).reduce(
    (acc, item) => acc + item.elapsed_size + item.remaining_size,
    0
  );
  // console.log('totalOnGoingUploadSize', totalOnGoingUploadSize);

  // ============================================================
  // Mobile Upload SSE — auto-subscribe on app load if session exists
  // Mirrors gift flow's subscribeToGiftUploadStatus pattern
  // ============================================================
  const mobileUploadSSERef = useRef(null);
  const processedMobileMessages = useRef(new Set());
  // Cache filename→thumbnail so it survives manager auto-removal (5s).
  // When fileUpload SSE is overwritten (race), recovery can still get the thumb.
  const mobileThumbCacheRef = useRef({});
  const setAppSnackbarRef = useRef(null);

  // Shared SSE message handler (used by both on-load and event-based subscriptions)
  const handleMobileUploadSSEMessage = useCallback((data) => {
    console.log('[MobileUpload SSE] ← Raw message received:', JSON.stringify(data));

    const statusData = data.parsedStatusData || data.status_data;
    if (!statusData) {
      console.log('[MobileUpload SSE] No status_data in message, skipping');
      return;
    }

    let parsed;
    try {
      parsed = typeof statusData === 'string' ? JSON.parse(statusData) : statusData;
    } catch (e) {
      console.error('[MobileUpload SSE] Failed to parse status_data:', e);
      return;
    }

    console.log('[MobileUpload SSE] Parsed status_data:', JSON.stringify(parsed));

    // Skip contribute flow messages — handled by contribute page's own SSE listener
    if (parsed.contribution) return;

    const { action, fileId, fileName } = parsed;

    // Dedup: prevent processing same message twice (SSE replays on reconnect)
    // Same pattern as gift flow's processedPushMessages
    const filesKey = parsed.files && Array.isArray(parsed.files) ? parsed.files.sort().join(',') : '';
    const messageId = `${action}_${fileId || ''}_${filesKey}_${data.timestamp || ''}`;
    if (processedMobileMessages.current.has(messageId)) {
      console.log('[MobileUpload SSE] SKIPPED (dedup):', messageId);
      return;
    }
    processedMobileMessages.current.add(messageId);
    if (processedMobileMessages.current.size > 100) {
      const items = Array.from(processedMobileMessages.current);
      processedMobileMessages.current = new Set(items.slice(-100));
    }

    console.log('[MobileUpload SSE] ▶ Processing action:', action, '| fileId:', fileId, '| fileName:', fileName);

    // Handle batch file notifications (fileUpload1, fileUpload2, etc.) — same as gift flow
    if (action && action.startsWith('fileUpload') && parsed.files && Array.isArray(parsed.files)) {
      console.log('[MobileUpload SSE] → Batch file notification:', action, '| files:', parsed.files);
      const thumbMap = parsed.thumbs || {};
      // Cache thumbnails so they survive manager auto-removal
      Object.entries(thumbMap).forEach(([name, thumb]) => {
        if (thumb) mobileThumbCacheRef.current[name] = thumb;
      });
      // Create remote upload entries for each file in the batch
      parsed.files.forEach((entry) => {
        // Parse "filename|uid" format (same as gift flow)
        const pipeIdx = entry.lastIndexOf('|');
        const fName = pipeIdx > 0 ? entry.substring(0, pipeIdx) : entry;
        const fId = pipeIdx > 0 ? entry.substring(pipeIdx + 1) : `batch-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        const thumbForFile = thumbMap[fName] || null;

        // Only create if not already tracked
        const existing = uploadManager.uploads.get(fId);
        if (!existing) {
          console.log('[MobileUpload SSE] → Creating batch entry:', { fId, fName });
          uploadManager.createRemoteUpload({ id: fId, fileName: fName, totalBytes: 0, thumbnail: thumbForFile });
        } else {
          console.log('[MobileUpload SSE] → Batch entry already exists, skipping:', fId);
        }
      });
      return;
    }

    switch (action) {
      case 'fileComplete': {
        // Cumulative file list — same as gift flow. files[] contains ALL completed files so far.
        const completedFiles = parsed.files || [];
        console.log('[MobileUpload SSE] → fileComplete (cumulative):', completedFiles);

        // s3Keys map: { fileId: s3Key } for all completed files
        const s3KeysMap = parsed.s3Keys || {};

        completedFiles.forEach((entry) => {
          const pipeIdx = entry.lastIndexOf('|');
          const fName = pipeIdx > 0 ? entry.substring(0, pipeIdx) : entry;
          const fId = pipeIdx > 0 ? entry.substring(pipeIdx + 1) : null;

          if (fId) {
            const existing = uploadManager.uploads.get(fId);
            if (!existing) {
              // Get thumbnail from cache or another manager entry with same filename
              let thumbnail = mobileThumbCacheRef.current[fName] || null;
              if (!thumbnail) {
                uploadManager.uploads.forEach((task) => {
                  if (task.fileName === fName && task.thumbnail && !thumbnail) {
                    thumbnail = task.thumbnail;
                  }
                });
              }
              uploadManager.createRemoteUpload({ id: fId, fileName: fName, totalBytes: 0, thumbnail });
            }
            if (!existing || existing.status !== 'completed') {
              console.log('[MobileUpload SSE] → Marking complete:', { fId, fName, hasS3Key: !!s3KeysMap[fId] });
              const extras = s3KeysMap[fId] ? { s3Key: s3KeysMap[fId] } : {};
              uploadManager.completeRemoteUpload(fId, extras);
            }
          }
        });
        break;
      }
      case 'allComplete': {
        console.log('[MobileUpload SSE] → allComplete. fileCount:', parsed.fileCount);
        const allS3Keys = parsed.s3Keys || {};

        // Create any missing entries from cumulative file list (safety net)
        if (parsed.files && Array.isArray(parsed.files)) {
          parsed.files.forEach((entry) => {
            const pipeIdx = entry.lastIndexOf('|');
            const fName = pipeIdx > 0 ? entry.substring(0, pipeIdx) : entry;
            const fId = pipeIdx > 0 ? entry.substring(pipeIdx + 1) : null;
            if (fId && !uploadManager.uploads.has(fId)) {
              // Get thumbnail from cache or another manager entry with same filename
              let thumbnail = mobileThumbCacheRef.current[fName] || null;
              if (!thumbnail) {
                uploadManager.uploads.forEach((task) => {
                  if (task.fileName === fName && task.thumbnail && !thumbnail) {
                    thumbnail = task.thumbnail;
                  }
                });
              }
              console.log('[MobileUpload SSE] → allComplete recovery: creating missing entry:', { fId, fName, hasCopiedThumb: !!thumbnail });
              uploadManager.createRemoteUpload({ id: fId, fileName: fName, totalBytes: 0, thumbnail });
            }
          });
        }

        // Mark any remaining entries as complete with their s3Keys
        uploadManager.uploads.forEach((task, taskId) => {
          if (task.uploadType === 'remote' && task.status !== 'completed') {
            const extras = allS3Keys[taskId] ? { s3Key: allS3Keys[taskId] } : {};
            uploadManager.completeRemoteUpload(taskId, extras);
          }
        });
        // Session done — clean up
        localStorage.removeItem('mobileUploadSessionId');

        // Show snackbar notification
        if (setAppSnackbarRef.current) {
          setAppSnackbarRef.current({
            isVisible: true,
            type: 'success',
            message: 'Mobile upload completed',
          });
        }

        // Remote uploads auto-remove via AUTO_HIDE_DELAY in completeRemoteUpload
        break;
      }
      default:
        console.log('[MobileUpload SSE] Unknown action:', action, parsed);
    }
  }, []);

  // Subscribe to SSE for a given session
  const connectMobileUploadSSE = useCallback((sseSessionId) => {
    const token = localStorage.getItem('token');
    if (!sseSessionId || !token) return;

    // Clean up previous subscription
    if (mobileUploadSSERef.current) {
      console.log('[MobileUpload SSE] Cleaning up previous subscription');
      mobileUploadSSERef.current();
      mobileUploadSSERef.current = null;
    }
    processedMobileMessages.current.clear();

    console.log('[MobileUpload SSE] Subscribing to session:', sseSessionId);

    const cleanup = subscribeToUploadStatus(sseSessionId, token, {
      onOpen: () => {
        console.log('[MobileUpload SSE] ✓ Connected successfully | session:', sseSessionId);
      },
      onMessage: handleMobileUploadSSEMessage,
      onError: (error) => {
        console.error('[MobileUpload SSE] ✗ Connection error:', error);
      },
    });

    mobileUploadSSERef.current = cleanup;
  }, [handleMobileUploadSSEMessage]);

  // Auto-subscribe on app load if session exists in localStorage
  useEffect(() => {
    const existingSessionId = localStorage.getItem('mobileUploadSessionId');
    if (existingSessionId) {
      console.log('[MobileUpload SSE] Found existing session on app load:', existingSessionId);
      connectMobileUploadSSE(existingSessionId);
    } else {
      console.log('[MobileUpload SSE] No active session found in localStorage');
    }

    return () => {
      if (mobileUploadSSERef.current) {
        console.log('[MobileUpload SSE] App unmounting, cleaning up SSE');
        mobileUploadSSERef.current();
        mobileUploadSSERef.current = null;
      }
    };
  }, [connectMobileUploadSSE]);

  // Listen for new sessions created by QR modal
  useEffect(() => {
    const handleNewSession = (e) => {
      const { sessionId: newSessionId } = e.detail || {};
      if (newSessionId) {
        console.log('[MobileUpload SSE] New session event received:', newSessionId);
        connectMobileUploadSSE(newSessionId);
      }
    };

    const handleClearSession = () => {
      console.log('[MobileUpload SSE] Clear session event received');
      if (mobileUploadSSERef.current) {
        mobileUploadSSERef.current();
        mobileUploadSSERef.current = null;
      }
      localStorage.removeItem('mobileUploadSessionId');
    };

    window.addEventListener('mobileUploadSessionCreated', handleNewSession);
    window.addEventListener('mobileUploadSessionCleared', handleClearSession);
    return () => {
      window.removeEventListener('mobileUploadSessionCreated', handleNewSession);
      window.removeEventListener('mobileUploadSessionCleared', handleClearSession);
    };
  }, [connectMobileUploadSSE]);

  const [plansPage, setPlansPage] = useState(false);
  const [autoSigningIn, setAutoSignIn] = useState(true);
  const [fetchDraftID, setfetchDraftID] = useState();
  const addVideoUpdater = useSelector((state) => state.addVideo);
  const [draftCategoryState, setDraftCategoryState] = useState({});
  // console.log('draftCategoryState', draftCategoryState);
  const [options, setOptions] = useState([]);
  const [userDetails, setUserDetails] = useState({
    firstname: '',
    lastname: '',
    display_name: '',
    dpURL: '',
    email: '',
    mobile: '',
    isNewUser: undefined,
    emailNS: undefined,
    subscriptionNS: undefined,
    totalVideoUploaded: undefined,
    totalContentUploaded: undefined,
    totalPhotoUploaded: undefined,
    mfa: undefined,
    trial_used: undefined,
    isLoginUsingSocial: undefined,
  });
  const [formData, setFormData] = useState(new FormData());
  const [videoGroup, setVideoGroup] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [userConnections, setUserConnections] = useState([]);
  // The logged-in user's OWN projector settings (projector_background /
  // view_type / thumbnail_style) — the TOP-LEVEL `settings` from
  // getAllViewRequestSent. Each connection's own settings live on the connection
  // object (userConnections[i].settings) for viewing that person's projector.
  const [myProjectorSettings, setMyProjectorSettings] = useState(null);
  const [loadingForAccess, setloadingForAccess] = useState(false);
  const [preferredEmail, setPreferredEmail] = useState('');
  const [isCompleted, setisCompleted] = useState('nothing');
  const [preferredUserDetails, setPreferredUserDetails] = useState({
    firstName: '',
    lastName: '',
  });
  const [uploadContainer, setUploadContainer] = useState([]);
  const [uploadContainer2, setUploadContainer2] = useState([]);
  const [isInActiveUser, setInActiveUser] = useState(false);
  const [currentSelectedPlan, setCurrentSelectedPlan] = useState({
    type: undefined,
    selected: { isTrial: undefined, selectedPlan: undefined },
  });
  const [appSnackbar, setAppSnackbar] = useState(defaultAppSnackbar);
  setAppSnackbarRef.current = setAppSnackbar;
  const [isViewerListUpdated, setViewerListUpdate] = useState(false);
  const [allPlans, setAllPlans] = useState();
  const [draftContent, setDraftContent] = useState([]);
  const [videoUploadedParts, setVideoUploadedParts] = useState([]);
  const {
    response: plansRes,
    error: plansErr,
    loading: plansLoading,
    refetch: refetchPlans,
  } = useMyAxios(getAllActivePlans, null, 'ALL_ACTIVE_PLANS');

  function base64ToBinary(base64String) {
    const [header, base64Data] = base64String.split(',');
    // Decode base64 into binary
    const binaryString = atob(base64Data);
    const len = binaryString.length;
    const binaryData = new Uint8Array(len);
    // eslint-disable-next-line
    for (let i = 0; i < len; i++) {
      binaryData[i] = binaryString.charCodeAt(i);
    }
    const mimeType = header.match(/:(.*?);/)[1];
    return new Blob([binaryData], { type: mimeType });
  }
  const updateUploadedData = (draft_id, data) => {
    dispatch(saveUploadInfo({ [draft_id]: data }));
  };
  const updateUploadDetails = (draft_id, data) => {
    dispatch(saveUploadDetails({ [draft_id]: data }));
  };
  const [uploaderLoader, setUploaderLoader] = useState(false);
  const [holdWhileDelete, setholdWhileDelete] = useState(false);
  const { mutate: autoSignIn } = useSWR([endpoints.getMyProfile], {
    onSuccess: ({ success, data, api_response, features }) => {
      if (success) {
        // Drive the Shared Folders feature off the backend flag.
        setSharedFeatureEnabled(features?.share_folders);
        // (The three viewer settings — thumbnail_style / view_type /
        // projector_background — are seeded from getAllViewRequestSent's
        // top-level `settings`, the single source of truth for my projector.)
        if (api_response?.access) {
          localStorage.setItem('uploader_token', api_response.access);
          try {
            const payload = JSON.parse(atob(api_response.access.split('.')[1]));
            if (payload.exp) {
              localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
            }
          } catch (e) {
            // ignore — token still usable, server is source of truth on expiry
          }
        }
        if (api_response?.uuid) {
          localStorage.setItem('user_uuid', api_response.uuid);
        }
        // Persist the email so the (non-reactive) new-UI gate in hooks/useNewUI
        // can allow-list it synchronously, even before AppContext is populated.
        try {
          localStorage.setItem('userEmail', (data?.email || '').toLowerCase());
        } catch (e) { /* storage blocked — gate falls back to classic */ }
        setUserDetails({
          ...userDetails,
          firstname: data?.firstname,
          lastname: data?.lastname,
          display_name: data?.display_name,
          dpURL: data?.image,
          email: data?.email,
          mobile: data?.mobile,
          isNewUser: data?.isNewUser,
          emailNS: data?.email_notification === '1',
          subscriptionNS: data?.sub_notification === '1',
          totalVideoUploaded: data?.totalVideoUploaded,
          totalContentUploaded: data?.totalContentUploaded,
          totalPhotoUploaded: data?.totalPhotoUploaded,
          // Onboarding checklist: JSON array of section keys the user has explored.
          explored: data?.explored,
          myId: data?.id,
          mfa: data?.mfa,
          isSubscription: data?.subscription,
          trial_used: data?.trials_used,
          isLoginUsingSocial: data?.apple_connected === '1' || data?.google_connected === '1',
          appleAccountConnected: data?.apple_connected,
          googleAccountConnected: data?.google_connected,
          // Viewer preferences (projector_background / view_type) used to theme
          // the owner's own /profile + viewer sub-pages.
          settings: data?.settings,
          projector_background: data?.projector_background,
        });
        localStorage.setItem('id', data?.id);
        setAutoSignIn(false);
        setUserSignInStatus(true);
        setUploaderLoader(false);

        // ── Mobile-OTP gate DISABLED ──────────────────────────────────────
        // This profile-load gate used to force email/password accounts with
        // `verify_mobile === 0` (present and not 1) to the OTP screen
        // (Pathname.authentication.newverifyCode). Disabled per request: an
        // unverified mobile no longer bounces the user to verification.
        //
        // The original gate is preserved below (commented out) for easy revert.
        // NOTE: the companion check `checkVerificationAfterLogin` in
        // Pages/public/authentication/newSign-in still enforces this on the
        // sign-in path — disable it there too if the redirect should be gone
        // everywhere.
        //
        // const mobileIsUnverified = data?.verify_mobile !== undefined
        //   && parseInt(data.verify_mobile, 10) !== 1;
        // const passwordIsSet = Boolean(data?.password)
        //   && String(data.password).trim() !== '';
        // const isTestException = isOtpVerificationTestException(data?.email);
        // const currentPath = window.location.pathname.toLowerCase();
        // const onExemptRoute = verificationExemptPaths.some(
        //   (path) => currentPath.startsWith(path.toLowerCase())
        // );
        // const onGiftFlowRoute = giftFlowPathPrefixes.some(
        //   (path) => currentPath.startsWith(path)
        // );
        // if (
        //   mobileIsUnverified
        //   && passwordIsSet
        //   && !isTestException
        //   && !onExemptRoute
        //   && !onGiftFlowRoute
        //   && data?.mobile
        //   && sessionStorage.getItem('isDesktopApp') !== 'true'
        //   && !sessionStorage.getItem('otpFlowCompletedPath')
        // ) {
        //   if (data?.email) {
        //     sessionStorage.setItem(encryptedKeyForCredentials.email, data.email);
        //   }
        //   if (data?.mobile) {
        //     sessionStorage.setItem(encryptedKeyForCredentials.mobile, data.mobile);
        //   }
        //   navigateTo(Pathname.authentication.newverifyCode);
        // }
      } else {
        // removeToken();
        setUploaderLoader(false);
        setAutoSignIn(false);
        // removeEmailVerified();
      }
    },
    onError: () => {
      removeToken();
      setAutoSignIn(false);
      setUploaderLoader(false);
      removeEmailVerified();
      // Don't reload if we're on autoLogin page to prevent infinite loops
      if (!window.location.pathname.includes('autoLogin')) {
        window.location.reload();
      }
    },
  });
  const warningTitle = userDetails?.appleAccountConnected === '1'
    ? 'Apple Account Connected'
    : userDetails?.googleAccountConnected === '1'
      ? 'Google Account Connected'
      : 'Social Account Connected';

  const warningMessage = userDetails?.appleAccountConnected === '1'
    ? 'This account is connected through Apple login. Updating the email address will unlink it from your Apple account.'
    : userDetails?.googleAccountConnected === '1'
      ? 'This account is connected through Google login. Updating the email address will unlink it from your Google account.'
      : 'This account is connected through a social login. Updating the email address will unlink it from the associated social account.';
  const processedDraftIdsRef = useRef(new Set());
  const [getAccessChecker, setGetAccessChecker] = useState();
  const getAccessCheckerRef = useRef(getAccessChecker);
  useEffect(() => { getAccessCheckerRef.current = getAccessChecker; }, [getAccessChecker]);
  const navigateToInviteFirstTime = (contentCount) => {
    const alreadyInvited = localStorage.getItem('inviteUser') === 'true';
    const totalUploaded = contentCount ?? userDetails?.totalContentUploaded;
    // console.log('navigateToInviteFirstTime - contentCount:', contentCount);
    // console.log('navigateToInviteFirstTime - userDetails?.totalContentUploaded:', userDetails?.totalContentUploaded);
    // console.log('navigateToInviteFirstTime - totalUploaded:', totalUploaded);
    // console.log('navigateToInviteFirstTime - alreadyInvited:', alreadyInvited);
    // console.log('navigateToInviteFirstTime - condition result:', (totalUploaded === 0 || totalUploaded === 1) && !alreadyInvited);
    if (
      (totalUploaded === 0 || totalUploaded === 1)
      && !alreadyInvited
      && getAccessCheckerRef.current?.success !== true
    ) {
      // console.log('navigateToInviteFirstTime - Navigating to ecosystem');
      setTimeout(() => {
        navigateTo(Pathname.authentication.ecosystem);
        localStorage.removeItem('pendingSingleUpload');
      }, 100);
      localStorage.setItem('inviteUser', 'true');
    }
  };

  useEffect(() => {
    const uploadVideoAndSignIn = async () => {
      const shouldProcess = addVideoUpdater[fetchDraftID] === true
        && isS3UploadComplete[fetchDraftID] === true
        && !processedDraftIdsRef.current.has(fetchDraftID);

      if (shouldProcess) {
        processedDraftIdsRef.current.add(fetchDraftID);

        const category_id = draftCategoryState[fetchDraftID]?.category_id;
        const category_name = draftCategoryState[fetchDraftID]?.category_name;
        const thumbnailx = draftCategoryState[fetchDraftID]?.thumbnailx;
        const customThumbnails = draftCategoryState[fetchDraftID]?.customThumbnails;
        const title = draftCategoryState[fetchDraftID]?.title;
        const subcategory_id = draftCategoryState[fetchDraftID]?.subcategory_id;
        const thumbnail_list = draftCategoryState[fetchDraftID]?.thumbnail_list;
        const visibility = draftCategoryState[fetchDraftID]?.visibility;
        const description = draftCategoryState[fetchDraftID]?.description;
        const videoWidth = draftCategoryState[fetchDraftID]?.videoWidth;
        const videoHeight = draftCategoryState[fetchDraftID]?.videoHeight;
        const videoOreintation = draftCategoryState[fetchDraftID]?.videoOreintation;
        const playlist_ids = draftCategoryState[fetchDraftID]?.playlist_ids;
        const video_duration = draftCategoryState[fetchDraftID]?.video_duration;
        const group_ids = draftCategoryState[fetchDraftID]?.videoGroup;

        category_id && formData.append('category_id', category_id);
        if (category_name) {
          formData.append('category_name', category_name);
        }
        formData.append('title', title);
        subcategory_id && formData.append('subcategory_id', subcategory_id);
        formData.append('visibility', visibility);
        formData.append('description', description);
        formData.append('videoWidth', videoWidth);
        formData.append('videoHeight', videoHeight);
        formData.append('videoOreintation', videoOreintation);
        formData.append('playlist_ids', playlist_ids);
        formData.append('video_duration', video_duration);
        formData.append('group_ids', group_ids);
        try {
          if (thumbnailx !== noPhotoAvailable && customThumbnails?.length > 0) {
            formData.append('thumbnails[]', base64ToBinary(thumbnailx));
          }
          if (thumbnailx !== noPhotoAvailable) {
            formData.append('thumbnail', base64ToBinary(thumbnailx));
          }
        } catch (error) {
          console.error(error.message);
        }
        if (
          customThumbnails?.length > 0
          && thumbnailx !== customThumbnails[0]
        ) {
          formData.append('thumbnails[]', base64ToBinary(customThumbnails[0]));
        }

        thumbnail_list.forEach((file, index) => {
          formData.append(
            'thumbnails[]',
            file,
            `thumbnail_${index + (customThumbnails?.length > 0 ? 2 : 1)}.jpg`
          );
        });
        try {
          await addVideo(formData);
          setUploadContainer2((prev) => prev?.filter((id) => id !== fetchDraftID));
          await autoSignIn();
          // Use pre-upload count from localStorage (stored when upload started)
          const preUploadCount = parseInt(localStorage.getItem('preUploadCount') || '0', 10);
          navigateToInviteFirstTime(preUploadCount);
          localStorage.removeItem('preUploadCount');
          setS3UploadComplete((prev) => ({ ...prev, [fetchDraftID]: false }));
          updateUploadedData(fetchDraftID, 'completed');
        } catch (error) {
          console.error('Error during upload or sign-in:', error);
        }
      }
    };

    uploadVideoAndSignIn();
  }, [addVideoUpdater[fetchDraftID], isS3UploadComplete[fetchDraftID]]);

  const { isValidating: gettingUserConnections, mutate: getUserConnections } = useSWR([endpoints.getAllViewRequestSent, 1], {
    fetcher: (url, status) => dataFetcher(url, { status }),
    onSuccess: ({ success, data, settings }) => {
      if (success) {
        setUserConnections(data);
        // Top-level settings = my own projector. Kept so the /content-layout
        // preferences + own /profile source all three viewer settings from here.
        setMyProjectorSettings(settings || null);
      } else {
        setUserConnections([]);
      }
      setloadingForAccess(false);
    },
    onError: () => {
      setUserConnections([]);
      setloadingForAccess(false);
    },
  });
  // Seed the three OWN viewer settings from getAllViewRequestSent's top-level
  // `settings` into the per-user localStorage the display + preferences read.
  // Gated on userDetails.myId so the localStorage keys (…<id>) resolve to the
  // real user id (set by getMyProfile) rather than "anon".
  useEffect(() => {
    if (!myProjectorSettings || !userDetails?.myId) return;
    let id = 'anon';
    try { id = localStorage.getItem('id') || 'anon'; } catch (e) { /* noop */ }
    try {
      // thumbnail_style → global tile-style key
      setThumbnailTileStyle(myProjectorSettings.thumbnail_style);
      // view_type ('fullscreen' | 'popup' | 'default') → viewer mode
      const mode = myProjectorSettings.view_type === 'fullscreen' ? 'fullscreen' : 'popover';
      localStorage.setItem(`projector.viewerMode.${id}`, mode);
      // projector_background ('default'/'' = aurora) → profile bg
      const bg = normalizeBg(myProjectorSettings.projector_background);
      if (bg) localStorage.setItem(`projector.profileBg.${id}`, bg);
      else localStorage.removeItem(`projector.profileBg.${id}`);
      // Let a mounted /profile pick up the background immediately.
      try { window.dispatchEvent(new CustomEvent('profileBg:change', { detail: bg || '' })); } catch (e) { /* noop */ }
    } catch (e) { /* localStorage unavailable — in-memory values still apply */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myProjectorSettings, userDetails?.myId]);
  const [toastForGroup, setToastForGroup] = useState('');
  const [subscriptionDetails, setSubscriptionData] = useState();
  const [subDetails, setSubDetails] = useState();
  const [langText, setLangText] = useState(undefined);
  const [mangerFunctionData, setmangerFunctionData] = useState(false);
  const [messaged, setmessaged] = useState('');
  const [referralStatus, setReferralStatus] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const browserLang = detectBrowserLanguage();
  const [showProgressBox, setProgressBox] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState([]);
  const [uploadingVideoDetails, setUploadingVideoDetails] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(undefined);
  const [uploadStatus, setUploadStatus] = useState(undefined);
  const [showDownloadBox, setDownloadBox] = useState(false);
  // True while a bulk (all videos/photos) download is in flight. Lives here so
  // the DownloadProgressBox can suppress its auto-close while videos have all
  // finished but album photos are still being fetched/queued (otherwise the
  // panel closes during that gap and the album downloads happen invisibly).
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState([]);
  const [downloadBatchProgress, setDownloadBatchProgress] = useState({ current: 0, total: 0 });
  // Download cancel/pause state
  const [downloadAbortControllers] = useState(new Map());
  const [pausedDownloads] = useState(new Set());
  const downloadAbortControllersRef = useRef(new Map());
  const pausedDownloadsRef = useRef(new Set());
  const resumeTriggersRef = useRef(new Map()); // Map of videoId -> resume function
  const bulkDownloadCancelRef = useRef(false); // Ref to signal bulk download cancellation
  const onDownloadPanelVisibleRef = useRef(null); // Ref to callback when download panel becomes visible
  const [visibility, setVisibility] = useState(true);
  const [videoDetail, setVideoDetail] = useState(undefined);
  const [startFlowEvent, startingFlowEvent] = useState(false);
  const [profileLoader, setProfileLoader] = useState(false);
  const [isSingleUpload, setSingleUpload] = useState(undefined);
  const [closeModal, setModalVisibility] = useState(false);
  const [uniqueId, setUniqueId] = useState(undefined);
  const [pageLoad, setPageLoad] = useState(false);
  const [videoCount, setVideoCount] = useState(0);
  const [refreshContent, setRefreshContent] = useState(false);
  const [errorMsg, setErrorMsg] = useState(undefined);
  const [selectedUploadFiles, setSelectedUploadFiles] = useState([]);
  const [s3Data, setS3Data] = useState({});
  const [uploadPercentages, setUploadPercentages] = useState([]);
  const [lineReq, setLineReq] = useState(false);
  const [apiCALL, setapiCALL] = useState(undefined);
  const [uploadingInProgress, setUploadingInProgress] = useState(null);
  const [toastVisiblity, setToastVisiblity] = useState(true);
  const [pubLoader, setPubLoader] = useState(false);
  const [iamUploading, setIamUploading] = useState(false);
  const [bigUserData, setbigUserData] = useState({});
  const [textOfUpload, settextOfUpload] = useState('Publish');
  const [invalidErr, setinvalidErr] = useState();
  const [isAddDraftDone, setisAddDraftDone] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const [helpNeed, setHelpNeed] = useState(false);
  const [dataOfCategory, setdataOfCategory] = useState();
  const [profileName, setProfileName] = useState();
  const [contextLoading, setContextLoading] = useState(false);
  const [singleEdit, setSingleEdit] = useState(false);
  const [amICalled, setamICalled] = useState(false);
  const [loader, setLoader] = useState(false);
  const [visible, setVisible] = useState(false);
  const [photoAlbumLoader, setphotoAlbumLoader] = useState(false);
  const [needToShowETA, setNeedToShowETA] = useState(false);
  const [theGrtLoader, settheGrtLoader] = useState(false);
  const [categoryLoader, setcategoryLoader] = useState(undefined);
  const [cardNumber, setCardNumber] = useState('');
  const [screatId, setScreatId] = useState('');
  const [cvvNumber, setCvvNumber] = useState('');
  const [expirationDate, setExpirationDate] = useState('');
  const [firstTimeOnlychecked, setFirstTimeOnlyChecked] = useState(false);
  const [cardAdded, setcardAdded] = useState(false);
  const [isTrail, setIsTrail] = useState('0');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [activeUploads, setActiveUploads] = useState([]);
  const [draftOfUpload, setDraftOfUpload] = useState([]);
  const [totalDraft, setTotalDraft] = useState([]);
  const [allvideoData, setAllvideoData] = useState([]);
  const [activeModalId, setActiveModalId] = useState(null);
  const [isUploadingBulky, setIsUploadingBulky] = useState(false);
  const [uploadProcessing, setUploadProcessing] = useState(false);
  const [disableDrag, setdisableDrag] = useState(false);
  const [didIOpenModal, setdidIOpenModal] = useState(false);
  const [openPaymentSuccess, setOpenPaymentSuccess] = useState(false);
  // True only between switch-flow payment success and the auto-redirect firing.
  // The success popup reads this to suppress its own Exit-button navigation, so the
  // pending setTimeout in expressCheckout is the sole navigator (avoids double-nav).
  const [isSwitchSuccess, setIsSwitchSuccess] = useState(false);
  const [openPaymentLoader, setOpenPaymentLoader] = useState(false);
  const [photoList, setPhotoList] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [overlay, setOverlay] = useState(false);
  const [trailVideos, setTrailVideos] = useState(10);
  const [trailModalVisible, setTrailPopUpModalVisible] = useState(false);
  const [selectedPlan, selectPlan] = useState(undefined);
  const [isFromAffl, setisFromAffl] = useState(false);
  const [affliatePlanId, setAffliatePlanId] = useState(11);
  const [expressloading, setExpressLoading] = useState(false);
  const [uploadType, setUpdateType] = useState(undefined);
  const [tabTitle, setTabTitle] = useState(
    parseInt(window.sessionStorage.getItem('tabTitle'), 10) || 0
  );

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const IsfreePlan = subscriptionDetails?.plans?.length > 0 ? subscriptionDetails.plans[subscriptionDetails.plans.length - 1]?.plan_id === '6' : subscriptionDetails?.subscription === null;
  const MAX_VIDEOS = subscriptionDetails?.subscription?.is_trial === '1' ? trailVideos : 100;
  const MAX_PHOTOS = 100;
  const storageUsedMb = subscriptionDetails?.storageUsed1 || 0; // in MB
  const freePlanLimitMb = 5 * 1024; // 5GB in MB
  const proPlanLimitMb = 30 * 1024; // 30GB in MB

  const remainingMb = IsfreePlan
    ? freePlanLimitMb - storageUsedMb
    : proPlanLimitMb - storageUsedMb;

  const MAX_SIZE_GB = parseFloat((remainingMb / 1024).toFixed(2));
  const MAX_SIZE_BYTES = MAX_SIZE_GB * 1024 * 1024 * 1024;
  const allowedExtensions = ['mp4', 'mov', 'mpg', 'm4v', 'm2ts'];
  const hasReachedStorageLimit = subscriptionDetails?.storageUsed?.total_storage >= freePlanLimitMb;
  // console.log('hasReachedStorageLimit', subscriptionDetails?.storageUsed?.total_storage, freePlanLimitMb);
  useEffect(() => {
    // Combine all `draft_id`s from `allvideoData`, default to empty arrays if undefined
    const allDraftIds = [
      ...(allvideoData?.video?.map((item) => item?.draft_id) || []),
      ...(allvideoData?.videoDraftContents?.map((item) => item?.draft_id)
        || []),
    ];
    // Filter out matching `draft_id`s from `draftOfUpload`
    setDraftOfUpload((prevDrafts) => prevDrafts?.filter((draftId) => !allDraftIds?.includes(draftId)));
    setTotalDraft(allDraftIds);
  }, [allvideoData]);

  const setLoadingState = (isLoading) => {
    setContextLoading(isLoading);
  };
  const updateUploadPercentages = (uploadPercentages) => {
    setUploadPercentages(uploadPercentages);
  };
  useEffect(() => {
    if (userDetails?.totalContentUploaded !== undefined) {
      const remainingUploads = Math.max(
        10 - (uploadContainer2?.length + userDetails?.totalContentUploaded),
        0
      );
      setTrailVideos(remainingUploads);
    }
  }, [userDetails?.totalContentUploaded, uploadContainer2]);
  const { isValidating: gettingVideoIdFromFlow, mutate: getVideoIdFromFlow } = useSWR([endpoints.getVideoIdFromFlow, uniqueId], {
    fetcher: (url, flowIdentifier) => dataFetcher(url, { flowIdentifier }),
    onSuccess: ({ success, video_id }) => {
      if (success) {
        const copyVideo = [...uploadingVideoDetails];
        const foundIndex = copyVideo.findIndex(
          (x) => x.fileId === uniqueId && x.id === null
        );
        if (foundIndex !== -1 && video_id) {
          copyVideo[foundIndex] = { ...copyVideo[foundIndex], id: video_id };
          // setUploadingVideoDetails(copyVideo);
        }
      } else {
        // setUserConnections([]);
      }
    },
    onError: () => {
      setUserConnections([]);
    },
  });

  useEffect(() => {
    if (!plansLoading && plansRes && plansRes?.data) {
      setAllPlans(plansRes?.data);
    }
  }, [plansRes, plansLoading]);

  // useEffect(() => {
  //   if (videoDetail) {
  //     setUploadingVideoDetails([...uploadingVideoDetails, videoDetail]);
  //     setVideoDetail(undefined);
  //   }
  // }, [videoDetail]);

  // useEffect(() => {
  //   const currentVideo = uploadingVideoDetails.find((x) => x.fileId === uniqueId);
  //   if (!currentVideo?.id && uniqueId) {
  //     getVideoIdFromFlow();
  //   }
  // }, [uploadingVideoDetails, uniqueId]);

  useEffect(() => {
    if (
      uploadingVideoDetails?.length
      && videoCount
      && !uploadingVideoDetails.filter((v) => v.id === null)?.length
    ) {
      setProgressBox(true);
      setModalVisibility(true);
      setVideoCount(0);
    } else {
      setModalVisibility(false);
    }
  }, [uploadingVideoDetails, videoCount]);
  useEffect(() => {
    if (uploadProgress) {
      const copyVideo = [...uploadingVideoDetails];
      const foundIndex = copyVideo.findIndex(
        (x) => x.fileId === uploadProgress.fileId
      );
      if (foundIndex !== -1) {
        copyVideo[foundIndex] = {
          ...copyVideo[foundIndex],
          progress: uploadProgress.progress,
          status: 'Uploading',
        };
        setUploadingVideoDetails(copyVideo);
      }
    }
  }, [uploadProgress]);
  useEffect(() => {
    if (isSingleUpload) {
      setUploadingVideoDetails([]);
    }
  }, [isSingleUpload]);

  useEffect(() => {
    if (uploadStatus) {
      const { progress, status, size, id, videoDuration, suggestedThumbnails } = uploadStatus;
      const copyVideo = [...uploadingVideoDetails];
      const foundIndex = copyVideo.findIndex(
        (x) => x.fileId === uploadStatus.fileId
      );
      if (foundIndex !== -1) {
        copyVideo[foundIndex] = {
          ...copyVideo[foundIndex],
          progress,
          status,
          size,
          id,
          videoDetail,
          suggestedThumbnails,
          videoDuration,
        };
        setUploadingVideoDetails(copyVideo);
      }
    }
  }, [uploadStatus]);

  const updateVideoDetails = (file, uniqueIdentifier) => {
    const newVideo = {
      progress: '0%',
      name: file.name,
      size: parseFloat((file.size / 1048576).toFixed(2)),
      fileId: uniqueIdentifier,
      status: 'Uploading',
      boxVisibility: 1,
      id: null,
      description: '',
      published: 0,
    };
    setUploadingVideoDetails((prevDetails) => [...prevDetails, newVideo]);
  };
  // TODO remove flowEventHandler completely
  const flowEventHandler = () => {
    setModalVisibility(false);
    const flow = new window.Flow({
      target: `${baseUrl?.URL}${endpoints.addVideoContentArray}`,
      testChunks: false,
      query: {
        token: getToken(),
      },
      fileParameterName: 'video_file[]',
    });
    if (flow?.support) {
      flow.assignBrowse(document.getElementById('video-file'));
      flow.on('fileAdded', (file, evt) => {
        startingFlowEvent(false);
        const foundIndex = uploadingVideoDetails.findIndex(
          (x) => x.fileId === file.uniqueIdentifier
        );
        if (foundIndex !== -1) {
          flow.off();
          startingFlowEvent(false);
          setModalVisibility(true);
          setErrorMsg('Video already added');
        } else {
          setErrorMsg(undefined);
          setVideoDetail({
            progress: '0%',
            name: file.name,
            size: parseFloat((file.size / 1048576).toFixed(2)),
            fileId: file.uniqueIdentifier,
            status: 'Uploading',
            boxVisibility: 1,
            id: null,
            description: '',
            published: 0,
          });
          if (evt?.target?.files?.length === 1 && !showProgressBox) {
            setSingleUpload(file.file);
            flow.off();
          } else {
            setUniqueId(file.uniqueIdentifier);
          }
        }
      });
      flow.on('filesAdded', (array, event) => {
        setVideoCount(array?.length);
      });
      flow.on('filesSubmitted', (arr, evt) => {
        flow.upload();
      });
      flow.on('fileProgress', (file, chunk) => {
        setUniqueId(file.uniqueIdentifier);
        let videoProgress = ((chunk.offset + 1) / file.chunks.length) * 100;
        videoProgress = `${parseInt(videoProgress, 10)}%`;
        setUploadProgress({
          fileId: file.uniqueIdentifier,
          progress: videoProgress,
        });
        setProgressBox(true);
      });
      // (C6) ON UPLOAD SUCCESS
      flow.on('fileSuccess', (file, message, chunk) => {
        const resp = JSON.parse(message);
        setUploadStatus({
          fileId: file.uniqueIdentifier,
          status: 'Completed',
          progress: '100%',
          id: resp?.video_id,
          videoDuration: resp?.video_duration,
          size: resp?.video_size,
          suggestedThumbnails: resp?.suggestedThumbnails,
        });
      });
      // (C7) ON UPLOAD ERROR
      flow.on('fileError', (file, message) => {
        setUploadStatus({
          fileId: file.uniqueIdentifier,
          status: 'Error',
        });
      });
      // flow.isComplete(() => {
      // });
      flow.on('complete', (message, file, chunk) => { });
    }
  };

  /**
   * Uploadfiles called whenever selectedUploadFiles changes, whcih will be changes based on modal input selection
   * check for the selectedUploadFiles array wether contains one or more files, and if >1 show progress box with each files progress
   */
  const uploadFiles = useCallback(() => {
    setModalVisibility(false);
    let callMultipleApi = true;
    selectedUploadFiles.forEach((selectedFile) => {
      const { file } = selectedFile;
      const formData = new FormData();
      if (selectedUploadFiles?.length === 1 && !showProgressBox) {
        setSingleUpload(selectedUploadFiles[0]?.file);
        callMultipleApi = false;
      } else {
        setUniqueId(selectedFile.uniqueIdentifier);
      }

      const foundIndex = uploadingVideoDetails.findIndex(
        (x) => x.fileId === selectedFile.uniqueIdentifier
      );
      if (foundIndex !== -1) {
        setModalVisibility(true);
        setErrorMsg('Video already added');
        callMultipleApi = false;
      } else {
        setErrorMsg(undefined);
        updateVideoDetails(file, selectedFile.uniqueIdentifier);
        formData.append('video_file[]', file);
      }
      if (callMultipleApi) {
        setVideoCount(1);
        setProgressBox(true);
        axios
          .post(`${baseUrl?.URL}${endpoints.addVideoContentArray}`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: getToken(),
            },
            onUploadProgress: (progressEvent) => {
              const { loaded, total } = progressEvent;
              const percent = Math.floor((loaded * 100) / total);
              setUploadProgress({
                fileId: selectedFile.uniqueIdentifier,
                progress: `${percent} %`,
              });
            },
          })
          .then((res) => {
            setUploadStatus({
              fileId: selectedFile.uniqueIdentifier,
              status: 'Completed',
              progress: '100%',
              id: res?.data?.video_id,
              videoDuration: res?.data?.video_duration,
              size: res?.data?.video_size,
              suggestedThumbnails: res?.data?.suggestedThumbnails,
            });
          })
          .catch((error) => {
            setUploadStatus({
              fileId: selectedFile.uniqueIdentifier,
              status: 'Error',
            });
          });
      }
    });
  }, [selectedUploadFiles]);
  const { isValidating: gettingSubscription, mutate: getSubscription } = useSWR(
    [endpoints.getMySubscription],
    {
      // Stamped with the token the request CARRIED, not the one in localStorage
      // when it comes back — an account switch mid-flight must leave the
      // response labelled as the old account's, so the payment gate and the
      // card dialogs can tell it isn't about the user now signed in.
      // See isSubscriptionForCurrentAccount in Utils/paymentGate.
      fetcher: async (url) => {
        const tokenAtRequest = getToken();
        const response = await dataFetcher(url);
        return stampSubscriptionAccount(response, tokenAtRequest);
      },
      onSuccess: (response) => {
        if (response.success) {
          const { subscription, planaction } = response;
          setSubDetails({ ...response });
          setInActiveUser(
            (!response?.has_activePlan
              && (response.expired || response.subscription !== null))
            || (response?.has_activePlan
              && parseInt(response?.subscription?.subscription_status, 10) === 2)
          );
          setReferralStatus(response?.referral_status);
        } else {
          setSubDetails({ subscription: { plan: 'Free' } });
        }
      },
      onError: () => {
        setSubDetails({ subscription: { plan: 'Free' } });
      },
    }
  );
  useEffect(() => {
    if (selectedUploadFiles.length > 0) {
      uploadFiles();
    }
  }, [selectedUploadFiles, uploadFiles]);
  // useEffect(() => {
  //   if (startFlowEvent) {
  //     flowEventHandler();
  //   }
  // }, [startFlowEvent]);
  const [stripeUiSelector, setStripeUiSelector] = useState();
  const [checked, setChecked] = useState(false);
  const clearChecked = () => {
    setChecked(false);
    setFirstTimeOnlyChecked(false);
  };
  const [expressData, setExpressData] = useState();
  const [planPurchasing, setPlanPurchasing] = useState(false);
  const expressCheckout = async (
    Id,
    plan_id = localStorage.getItem('planId'),
    closeAddCard,
    managerTokenParam = null
  ) => {
    setOpenPaymentLoader(true);
    const userData = apiCALL?.data?.userDetails[0] || bigUserData;
    const userId = userData?.id || userData?.userId || localStorage.getItem('temp_user_id');
    // Switch-plan flow flag (set by SwitchPlan.jsx before navigating manager to /pay-by-manger).
    const rawPaymentMode = localStorage.getItem('paymentMode');
    const isSwitchMode = rawPaymentMode === 'switch';
    const subscribeEndpoint = isSwitchMode ? endpoints.switchPlan : endpoints.addPlanV2;
    // console.log('%c[SWITCH-DEBUG] 10. expressCheckout ENTRY', 'color:#9333ea;font-weight:bold', { paymentMode: rawPaymentMode, isSwitchMode, endpoint: subscribeEndpoint, managerTokenParam_truthy: !!managerTokenParam, managerTokenParam });
    try {
      const myToken = await getToken();
      const tokenForPurchase = managerTokenParam || myToken;
      // console.log('%c[SWITCH-DEBUG] TOKEN-Z. FINAL token choice in expressCheckout', 'color:#9333ea;font-weight:bold;font-size:14px', { managerTokenParam, myToken_localStorage: myToken, tokenForPurchase_SENT_IN_BODY: tokenForPurchase, willUseFetch: !!managerTokenParam });
      // if (managerTokenParam && tokenForPurchase === myToken) {
      //   console.log('%c[SWITCH-DEBUG] TOKEN-Z⚠. managerTokenParam was truthy but EQUAL to user token — unexpected', 'color:#dc2626;font-weight:bold');
      // }
      // if (!managerTokenParam) {
      //   console.log('%c[SWITCH-DEBUG] TOKEN-Z⚠. NO managerTokenParam — body.token will be USER token from localStorage', 'color:#dc2626;font-weight:bold;font-size:14px');
      // }
      const apiUrl = `${baseUrl}/${subscribeEndpoint}`;
      const requestBody = {
        promo: localStorage.getItem('promocode'),
        ...(localStorage.getItem('affiliate') !== 'undefined' && {
          affiliateCode: localStorage.getItem('affiliate'),
        }),
        token: tokenForPurchase,
        ...(localStorage.getItem('affiliate') !== 'undefined'
          && localStorage.getItem('firstTimeOnlychecked') === 'true'
          ? { only_first_year: true }
          : { only_first_year: false }),
        plan_id,
        is_free: 0,
        ...(isFromAffl && { for_affiliate: true }),
        ...(isFromAffl && {
          managerCard: localStorage.getItem('managerCard') === '1',
        }),
        //  ...(isFromAffl && { userId: localStorage.getItem('affliate_userId') }),
        ...(localStorage.getItem('affliate_userId')
          && localStorage.getItem('affliate_userId') !== 'undefined' && {
          userId: localStorage.getItem('affliate_userId'),
        }),
        trial: localStorage.getItem('trial') === '1',
        ...(Id && { checkout_id: Id }),
      };
      // Use fetch when manager token is provided to bypass axios interceptor
      let response;
      if (managerTokenParam) {
        const fetchResponse = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });
        const data = await fetchResponse.json();
        if (!fetchResponse.ok || data?.success === false) {
          const err = new Error(data?.message || 'Failed to add plan');
          err.response = { status: fetchResponse.status, data };
          throw err;
        }
        response = { data };
      } else {
        response = await axios.post(apiUrl, requestBody);
      }
      // console.log('Express Checkout Response:', response.data);

      if (window.gtag) {
        window.gtag('event', 'purchase', {
          transaction_id: new Date().getTime().toString(), // or use your own unique ID if available
          value: 0, // Trial = free (change if user paid)
          currency: 'USD', // Change to your actual currency
          coupon: '', // If any coupon applied
          items: [
            {
              item_id:
                response.data?.plan_name?.toLowerCase().replace(/\s+/g, '_')
                || 'subscription',
              item_name: response.data?.plan_name || 'Subscription Plan',
              price: response.data?.value,
              quantity: 1,
              trial: response.data?.isTrial,
              trial_duration: response.data?.plan_trial_duration,
              plan_duration: response.data?.plan_duration,
              start_date: new Date().getTime().toString(),
            },
          ],
        });
      }

      setExpressData(response?.data);
      setAppSnackbar({
        isVisible: true,
        type: 'success',
        message: response?.data?.message,
      });
      if (localStorage.getItem('affliate_userId')) {
        setPaymentStatus('affliate');
      } else {
        setPaymentStatus('success');
      }
      const isPendingSwitchMsg = isSwitchMode
        && typeof response?.data?.message === 'string'
        && response.data.message.toLowerCase().includes('request for plan switch has been received');
      if (!isPendingSwitchMsg) {
        setOpenPaymentSuccess(true);
      } else {
        setOpenPaymentLoader(false);
      }
      getSubscription();
      autoSignIn();
      setExpressLoading(false);
      // Switch-plan flow: return to billings page and clear the flag.
      // Soft-navigate (pushState + popstate) instead of window.location.href so the
      // App tree survives — otherwise mangerToken / userAccessedData (React state)
      // are wiped and the manager's NEXT switch attempt sees them as falsy and
      // falls into the normal-user modal flow instead of routing to /pay-by-manger.
      if (isSwitchMode) {
        localStorage.removeItem('paymentMode');
        setIsSwitchSuccess(true);
        // console.log('[SWITCH-DEBUG] 12. expressCheckout success — isSwitchMode branch. About to soft-navigate to /settings/billings-and-payments in 2s. managerTokenParam(closure)=', !!managerTokenParam);
        setTimeout(() => {
          // console.log('[SWITCH-DEBUG] 13. expressCheckout success — FIRING navigateTo(/settings/billings-and-payments) (soft nav, React state preserved).');
          navigateTo('/settings/billings-and-payments');
          // Note: isSwitchSuccess is NOT reset here — the popup is still open and
          // Exit might be clicked after this nav fired. Resetting here would let
          // Exit fall through to paymentStatus-based nav (e.g. /manage-users),
          // causing a second jump right as the popup closes. The flag is reset
          // when the popup actually closes (onHide on the <SuccessPopUp />).
        }, 2000);
        return;
      }
      // Navigate to dashboard when manager is accessing user after successful plan purchase
      // Show success popup first, then navigate after delay so user sees it.
      // Soft-navigate (navigateTo / pushState) instead of window.location.href so the
      // App tree survives — otherwise mangerToken / userAccessedData (React state)
      // are wiped and the manager's NEXT plan action (e.g. switch) sees them as
      // falsy and falls into the normal-user flow instead of routing to /pay-by-manger.
      if (managerTokenParam) {
        setTimeout(() => {
          navigateTo('/dashboard');
        }, 2000);
        return;
      }
      localStorage.removeItem('promocode');
      localStorage.removeItem('affiliate');
      localStorage.removeItem('trial');
      localStorage.removeItem('affliate_userId');
      localStorage.removeItem('managerCard');
      localStorage.removeItem('firstTimeOnlychecked');
      //  localStorage.removeItem('planId');
      setTimeout(() => {
        closeAddCard && closeAddCard();
        setPlanPurchasing(false);
      }, 2000);
      localStorage.setItem('tutorialCloud', true); // Set tutorialCloud to true to show tutorial modal when enetering in cloud
    } catch (err) {
      //  setTimeout(() => {
      //          closeAddCard();
      //      setPlanPurchasing(false);
      //  }, 2000);
      setOpenPaymentLoader(false);
      const responseData = err?.response?.data?.message || err?.message;
      logAppEvent({
        userName: `${userDetails?.firstname || ''} ${userDetails?.lastname || ''}`.trim() || 'Guest',
        functionAffected: 'Purchase Plan',
        description: 'Plan purchase failed at app level',
        severity: 'high',
        errorMessage: responseData || 'Plan purchase failed',
        responseStatus: err?.response?.status || null,
        responseBody: err?.response?.data ? JSON.stringify(err.response.data) : null,
        requestUrl: `${baseUrl}/${subscribeEndpoint}`,
        requestMethod: 'POST',
        component: 'App',
        userAction: 'Purchase plan called',
      });
      localStorage.removeItem('promocode');
      localStorage.removeItem('affiliate');
      localStorage.removeItem('trial');
      localStorage.removeItem('managerCard');
      localStorage.removeItem('planId');
      setExpressLoading(false);
      if (localStorage.getItem('affliate_userId')) {
        setPaymentStatus('affliate_error');
      } else {
        setPaymentStatus('error');
      }
      localStorage.removeItem('affliate_userId');
      autoSignIn();
      setExpressData(err?.response?.data);
      setOpenPaymentSuccess(true);
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message:
          responseData || 'Oops! Something went wrong while purchasing plan',
      });
    }
  };
  useEffect(() => {
    if (
      localStorage.getItem('token') === null
      && window.location.pathname === '/'
      && window.location.origin !== 'http://localhost:3000'
      && window.location.origin !== 'https://preprod.projector.app'
    ) {
      window.location.href = 'https://www.projectorstream.com';
    } else if (
      window.location.pathname.includes('success-payment')
      && new URLSearchParams(window.location.search).get('checkout_id')
    ) {
      const params = new URLSearchParams(window.location.search);
      const id = params.get('checkout_id');
      setTimeout(() => {
        expressCheckout(id);
      }, 100);
    } else if (window.location.pathname.includes('cancel')) {
      localStorage.removeItem('code');
    }
  }, []);
  useEffect(() => {
    if (subDetails && subDetails !== subscriptionDetails) {
      setSubscriptionData(subDetails);
    }
  }, [subDetails]);

  const { isValidating: gettingLangFile, mutate: getLanguageFile } = useSWR(
    [endpoints.getLangFile, browserLang],
    {
      fetcher: (url, lang) => dataFetcher(url, { lang }),
      onSuccess: (response) => {
        if (response.success) {
          setLangText(JSON.parse(response.data));
        } else {
          setLangText(undefined);
        }
      },
      onError: () => {
        setLangText(undefined);
      },
    }
  );
  const defaultCategoryName = 'All Videos';
  const [mangerToken, setMangerToken] = useState();
  const [topOrBottom, setTopOrBottom] = useState(true);
  const [userAccessedData, setuserAccessedData] = useState();
  const [managerPrivatePlanAccess, setManagerPrivatePlanAccess] = useState(false);
  const [managerPaymentMethods, setManagerPaymentMethods] = useState([]);
  const [apiResponse, setApiResponse] = useState();
  const [trialVal, setTrial] = useState(undefined);
  const [cardPayId, setcardPayId] = useState();
  const [cardUpdate, setcardUpdate] = useState(false);
  const [allowedEmails, setAllowedEmails] = useState();
  const [cardUpdateBoolean, setcardUpdateBoolean] = useState(false);
  const daysOfMonths = '30';
  const [isTokenChange, setIsTokenChange] = useState(false);
  const [affiliateCode, setAffiliatedCode] = useState('');
  const [encounterLoading, setEncounterLoading] = useState(false);
  // Co-branding: the current user's brand (their own, or the manager whose
  // account they belong to). When a BrandLogo is present we swap it in for the
  // Projector logo in the app headers. Best-effort — falls back to the app logo.
  const [brandLogoUrl, setBrandLogoUrl] = useState('');
  const [brandName, setBrandName] = useState('');
  const fetchBrandConfig = async () => {
    try {
      const myToken = await getToken();
      const { data } = await axios.post(`${baseUrl}/inviteUsersList`, { token: myToken });
      const list = Array.isArray(data?.data) ? data.data : [];
      const configs = list.find((u) => u?.configs?.BrandLogo)?.configs;
      setBrandLogoUrl(configs?.BrandLogo || '');
      setBrandName(configs?.BrandName || '');
    } catch (error) {
      setBrandLogoUrl('');
    }
  };
  const handleClick = (e) => {
    const managerTokenNow = getToken();
    // console.log('%c[SWITCH-DEBUG] TOKEN-A. handleClick FIRED (impersonate)', 'color:#0891b2;font-weight:bold;font-size:14px', { capturedManagerToken: managerTokenNow, newUserToken: e?.token, userBeingAccessed: e?.id || e?.userId });
    // console.trace('[SWITCH-DEBUG] TOKEN-A. handleClick call stack');
    const currentUserPrivatePlanAccess = apiResponse?.data?.[0]?.private_plan_access;
    if (currentUserPrivatePlanAccess === '1' || currentUserPrivatePlanAccess === 1) {
      setManagerPrivatePlanAccess(true);
    }
    setMangerToken(managerTokenNow);
    // Also persist it: mangerToken is React state, so a refresh (or any hard
    // navigation) inside the accessed account loses it, and Exit then has
    // nothing to fall back on if the backend's stored manager_token is stale.
    if (isUsableToken(managerTokenNow)) {
      localStorage.setItem('managerOwnToken', managerTokenNow);
    }
    // Store manager's payment methods before switching
    setManagerPaymentMethods(subscriptionDetails?.paymentMethods || []);
    setuserAccessedData(e);
    const newToken = e.token;
    setToken(newToken);
    setIsTokenChange(true);
    // console.log('[SWITCH-DEBUG] TOKEN-B. handleClick done. localStorage.token NOW=', getToken(), ' (should equal newUserToken). mangerToken STATE will be=', managerTokenNow);
    // Clear inviteUser flag when switching to accessed user's account
    localStorage.removeItem('inviteUser');
    // Refresh the co-branding logo for the account we just switched into.
    fetchBrandConfig();
  };
  const handleManger = (e) => {
    // console.log('%c[SWITCH-DEBUG] TOKEN-X. 🚨 handleManger FIRED (exit impersonation) — THIS CLEARS mangerToken/userAccessedData', 'color:#dc2626;font-weight:bold;font-size:16px;background:#fef2f2', { localStorageToken_BEFORE: getToken(), restoringManagerToken: e?.data?.manager_token });
    // console.trace('[SWITCH-DEBUG] TOKEN-X. handleManger call stack — WHO CALLED THIS?');
    // Where the restored manager token comes from, in order of trust:
    //   1. mangerToken — captured from localStorage at switch-in, this session.
    //   2. managerOwnToken — the same value, persisted, so it survives a refresh.
    //   3. e.data.manager_token — /getManagerUserLogs. Same value in the healthy
    //      case, but it's a snapshot frozen in a DB row at switch-in time, so a
    //      stale/duplicate row (an access session that was never exited cleanly,
    //      or one belonging to another manager) hands back an expired or empty
    //      token. Installing that used to 401 the next /getMyProfile and hard-log
    //      the manager out with no way back — the accounts carrying such a row
    //      failed on every single exit.
    // If none is usable we abort the exit and clear nothing, so the manager stays
    // where they are and can retry instead of losing the session.
    const newToken = [mangerToken, localStorage.getItem('managerOwnToken'), e?.data?.manager_token]
      .find((candidate) => isUsableToken(candidate));
    if (!newToken) return false;
    // Clear manager's private_plan_access when returning to manager's own account
    setManagerPrivatePlanAccess(false);
    setMangerToken(null);
    setManagerPaymentMethods([]);
    setuserAccessedData(null);
    setToken(newToken);
    localStorage.removeItem('managerOwnToken');
    // console.log('[SWITCH-DEBUG] TOKEN-Y. handleManger done. localStorage.token AFTER=', getToken(), ' (should be manager token). mangerToken STATE cleared to null.');
    // Clear inviteUser flag when returning to manager's account
    localStorage.removeItem('inviteUser');
    // Reset payment-flow state that belongs to the impersonated user. Without this,
    // the "Use your account information" checkbox stays checked, leftover planId /
    // paymentMode / promo / affiliate values poison the next purchase, and
    // currentSelectedPlan can preselect a stale plan.
    clearChecked();
    setCurrentSelectedPlan({});
    setAffiliatedCode('');
    setapiCALL(undefined);
    setbigUserData({});
    localStorage.removeItem('paymentMode');
    localStorage.removeItem('planId');
    localStorage.removeItem('promocode');
    localStorage.removeItem('affiliate');
    localStorage.removeItem('affliate_userId');
    localStorage.removeItem('trial');
    localStorage.removeItem('managerCard');
    localStorage.removeItem('firstTimeOnlychecked');
    localStorage.removeItem('code');
    // Refresh the co-branding logo for the manager account we returned to.
    fetchBrandConfig();
    return true;
  };

  // Manager user switching function
  const switchingFunction = async (obj) => {
    // console.log('[SWITCH-DEBUG] 8a. switchingFunction entry. paymentMode=', localStorage.getItem('paymentMode'));
    try {
      const formData = new FormData();
      formData.append('token', getToken());
      formData.append('user_id', obj.id);
      formData.append('user_token', obj.token);
      const response = await axios.post(`${baseUrl}/managerUserLogs`, formData);
      // console.log('[SWITCH-DEBUG] 8a-2. switchingFunction — managerUserLogs returned. success=', response?.data?.success, ' paymentMode=', localStorage.getItem('paymentMode'));
      if (response?.data?.success === true) {
        // Clear all dummy/temporary data from Redux store
        // (dispatch would need to be added if not using from component)
        handleClick(obj);
        localStorage.setItem('firstname', obj?.firstname || '');
        localStorage.setItem('lastname', obj?.lastname || '');
        // Frontend impersonation markers — sidebar banner gate reads these
        // on top of /getUserAccess. Without them the "Accessed X" banner
        // won't render in the user-with-plan / checkout flows that funnel
        // through this switchingFunction.
        localStorage.setItem('isAccessingUser', 'true');
        localStorage.setItem('accessedUserId', String(obj?.id || ''));
        // console.log('[SWITCH-DEBUG] 8a-3. switchingFunction — about to navigateTo(/dashboard). paymentMode=', localStorage.getItem('paymentMode'));
        navigateTo('/dashboard');
        // console.log('[SWITCH-DEBUG] 8a-4. switchingFunction — AFTER navigateTo. paymentMode=', localStorage.getItem('paymentMode'));
        getSubscription();
        autoSignIn();
        return { success: true };
      }
      setmessaged(response?.data?.message);
      setmangerFunctionData(true);
      return { success: false, message: response?.data?.message };
    } catch (error) {
      console.error(error);
      return { success: false, error };
    }
  };

  /**
   * Get user profile using token as payload
   */
  const fetchWhitelistedEmails = async () => {
    try {
      const response = await axios.get(
        `${cloudBaseUrl}/user/whitelisted-emails/`
      );
      setAllowedEmails(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching whitelisted emails:', error);
      throw error;
    }
  };
  useEffect(() => {
    // fetchWhitelistedEmails();
  }, []);
  const isEmailAllowed = allowedEmails?.results?.some(
    (item) => item?.email?.toLowerCase() === userDetails?.email?.toLowerCase()
  );
  const forceAutoSignin = () => {
    setAutoSignIn(true);
    autoSignIn();
  };

  useEffect(() => {
    const detectBrowserLanguage = require('detect-browser-language');
    getLanguageFile();
    const htmlClass = document.getElementsByTagName('html')[0].classList;

    window.onresize = () => {
      htmlClass.add(styles.changingTheme);
      setTimeout(() => htmlClass.remove(styles.changingTheme), 500);
    };
  }, []);

  useEffect(() => {
    const token = getToken();
    const isEmailVerified = getEmailVerified();
    if (token) {
      setAutoSignIn(true);
      autoSignIn();
    } else {
      setAutoSignIn(false);
    }
  }, [autoSignIn]);
  const [isLoading, setIsLoading] = useState(true);
  const fetchData = async () => {
    try {
      const myToken = await getToken();
      const apiUrl = `${baseUrl}/affiliateDetails`;
      const requestBody = {
        token: myToken,
      };
      const response = await axios.post(apiUrl, requestBody);
      setApiResponse(response.data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (userIsSignedIn) {
      setloadingForAccess(true);
      getUserConnections();
      getSubscription();
      fetchBrandConfig();
    }
  }, [userIsSignedIn, getUserConnections]);
  const referralCode = localStorage.getItem('code');
  const [holdTheWeb, setHoldTheWeb] = useState(false);
  const disableScroll = () => {
    const scrollPosition = window.pageYOffset
      || document.documentElement.scrollTop
      || document.body.scrollTop;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollPosition}px`;
    document.body.style.width = '100%'; // Set width to 100%
  };
  const enableScroll = () => {
    const scrollPosition = parseInt(document.body.style.top || '0', 10);
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo(0, -scrollPosition);
  };
  useEffect(() => {
    holdTheWeb === true ? disableScroll() : enableScroll();
  }, [holdTheWeb]);
  const [uploadPercentagesInNumber, setUploadPercentagesInNumber] = useState();
  const [droper, setDroper] = useState(false);
  const [videoFiles, setVideoFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [pendingVideoFile, setPendingVideoFile] = useState(null);
  const [pendingPhotoFiles, setPendingPhotoFiles] = useState(null);
  const [addMorePhoto, setaddMorePhoto] = useState(false);
  const [uploadModalIsVisible, setUploadModalVisibility] = useState(false);
  const [popUpModalVisible, setPopUpModalVisible] = useState(false);
  const [openNewUploadModal, setOpenNewUploadModal] = useState(false);
  const [openPhotoUploadModal, setOpenPhotoUploadModal] = useState(false);
  // Cloud photo album modal state (persists across navigation)
  const [photoAlbumModalOpen, setPhotoAlbumModalOpen] = useState(false);
  const [pendingCloudPhotos, setPendingCloudPhotos] = useState([]);
  const [pendingCloudPhotoService, setPendingCloudPhotoService] = useState(null);
  const [showMultipleModal, setShowMultipleModal] = useState(false);
  const [showAddPlaylistModal, setShowAddPlaylistModal] = useState(false);
  const [showAddSubCategoryModal, setShowAddSubCategoryModal] = useState(false);
  // Global category-create modal: open from anywhere via setShowAddCategoryModal(true).
  // Pass an existing category as categoryModalEditData to open in edit mode.
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
  const [categoryModalEditData, setCategoryModalEditData] = useState(null);
  const [selectedFilesMulti, setSelectedFilesMulti] = useState([]);
  const [buttonText, setButtonText] = useState('Publish');
  const [navigateOnceClicked, setNavigateOnceClicked] = useState(false);
  const [editLoader, setEditLoader] = useState(false);
  const [profileDetails, setProfileDetails] = useState({
    firstname: undefined,
    lastname: undefined,
    email: undefined,
    image: undefined,
    type: undefined,
  });
  const [open, setOpen] = useState(false);
  const [openX, setOpenX] = useState(false);
  const [cloudToken, setCloudToken] = useState();
  const [categoryDetails, setCategoryDetails] = useState();
  const [albumDetails, setAlbumDetails] = useState();
  const [chooseAlbumStep, setChooseAlbumStep] = useState(0);
  const [loginWithSocial, setLoginWithSocial] = useState(false);
  const [findAllVideoCategory, setFindAllVideoCategory] = useState({
    value: '', name: defaultCategoryName,
  });
  const [selectedItemsInBulk, setSelectedItemsInBulk] = useState(Array(videoFiles?.length).fill({
    token: getToken(),
    group_ids: [],
    draftId: '',
    thumbnails: '',
    name: '',
    description: '',
    category: '',
    categoryName: '',
    playlist: [],
    visibility: '2',
    size: '',
  }));
  const handleOpen = () => setOpen(true);
  const handleOpenX = () => setOpenX(true);
  const handleSingleDrop = () => {
    setDroper(false);
    // setCrossDrag(false);
  };
  const revealUploadModal = (videoFiles) => {
    const totalSize = (videoFiles?.reduce((acc, file) => acc + file.size, 0) || 0) / (1024 * 1024);
    localStorage.setItem('opened_draft_id', null);
    autoSignIn().then((res) => {
      if (
        !res?.data?.subscription
        && res?.data?.totalContentUploaded >= ALLOWED_UPLOADS_COUNT
      ) {
        setPopUpModalVisible(true);
        setEditLoader(false);
        setOpen(false);
      } else {
        setUploadModalVisibility(true);
        setEditLoader(false);
      }
    });
    const invalidFiles = videoFiles.filter((file) => {
      const extension = file.name.split('.').pop().toLowerCase();
      return !allowedExtensions.includes(extension);
    });
    if (invalidFiles.length > 0) {
      setAppSnackbar({
        isVisible: true,
        type: 'error',
        message:
          'Invalid file format. Allowed formats are: .mp4, .mov, .mpg, .m4v, .m2ts',
      });
      // alert('Invalid file format. Allowed formats are: .mp4, .mov, .mpg, .m4v, .m2ts');
      return;
    }
    if (userDetails?.isSubscription === null) {
      const remainingUploads = ALLOWED_UPLOADS_COUNT - userDetails?.totalContentUploaded;
      if (
        videoFiles?.length <= remainingUploads
        && totalSize <= MAX_SIZE_BYTES
        && remainingUploads > 0
      ) {
        handleOpen();
      } else {
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: `${remainingUploads} free upload allowed`,
        });
        setPopUpModalVisible(true);
      }
    } else if (userDetails?.isSubscription !== null) {
      if (videoFiles?.length <= MAX_VIDEOS && totalSize <= proPlanLimitMb) {
        handleOpen();
      } else {
        const errorMessage = videoFiles?.length > MAX_VIDEOS
          ? `Maximum of ${MAX_VIDEOS} videos allowed`
          : `Total size of videos should not exceed ${MAX_SIZE_GB}`;
        setAppSnackbar({
          isVisible: true,
          type: 'error',
          message: errorMessage,
        });
        // console.log('totalSize', totalSize, MAX_SIZE_BYTES, proPlanLimitMb);
      }
    }
  };
  const paymentNavigation = (route) => {
    setOpenPaymentSuccess(false);
    navigateTo(route);
  };
  const dragOver = (e) => {
    e.preventDefault();
    setDroper(true);
  };
  const dragLeave = (e) => {
    e.preventDefault();
    setDroper(false);
  };
  const hideUploadModal = () => {
    setUploadModalVisibility(false);
  };
  const backendUrl = 'https://backend.projectorstream.com';
  // if (
  //   window.location.origin.includes('localhost')
  //   || window.location.origin.includes('preprod')
  // ) {
  //   backendUrl = 'https://preprod.backend.projector.app';
  // }
  const [orderArrange, setOrderArrange] = useState([]);
  const fetchLayoutOrder = async (profileID) => {
    try {
      const response = await axios.post(`${backendUrl}/getLayoutOrder`, {
        userId: profileID,
        token: getToken(),
      });

      // console.log('Layout Order Response:', response.data);
      setOrderArrange(response.data.data);
    } catch (error) {
      console.error('Error fetching layout order:', error);
    }
  };

  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [cardExpiryDialog, setCardExpiryDialog] = useState(false);
  const [fromLogin, setFromLogin] = useState(false);
  // A manager sitting inside a user's account must never see the
  // "Unlock your Projector" card dialogs — that's the user's billing, not the
  // manager's. getAccessChecker alone isn't enough: it's filled by an async
  // /getUserAccess call that the sidebar fires AFTER the switch, so right after
  // a manager creates a user (quick user / user with plan) and lands in that
  // fresh account, getSubscription() reports paymentMethods=[] while
  // getAccessChecker still holds the manager's (success !== true) response and
  // the dialog slips through. The synchronous impersonation markers set by
  // handleClick/switchingFunction close that window.
  // Same definition PaymentMethodGate uses, and for the same reason: a
  // getAccessChecker.success===true is NOT proof of impersonation (it's true
  // for any manager-created account, including on that user's own login), so
  // only the in-session state and the isAccessingUser marker count here.
  const isManagerImpersonating = !!mangerToken
    || !!userAccessedData
    || localStorage.getItem('isAccessingUser') === 'true';
  // ...and separately: an account whose bill belongs to a manager holds no card
  // by design, so "no card" is not a problem worth a dialog. This is what the
  // original `getAccessChecker?.success !== true` test was really protecting,
  // and dropping it in favour of the impersonation markers alone is what put
  // these dialogs (and the gate) in front of managers and manager-created users.
  // Both tests are needed — see PaymentMethodGate for why they differ.
  const isBilledElsewhere = !ownsItsOwnBilling(getAccessChecker)
    || isAffiliateUser(userDetails?.email);
  // Neither dialog belongs on an onboarding/auth route — see isOnboardingPath.
  const suppressCardDialogs = isManagerImpersonating
    || isBilledElsewhere
    || isOnboardingPath(window.location.pathname);
  function isCardExpiringSoon(exp_month, exp_year) {
    const expiryDate = new Date(exp_year, exp_month, 0);
    const warningDate = new Date(expiryDate);
    warningDate.setDate(expiryDate.getDate() - 7);
    const today = new Date();
    return today >= warningDate;
  }
  const expiryDateChecker = subDetails?.paymentMethods?.length > 0
    && subDetails?.paymentMethods?.find(
      (card) => card?.id === subDetails?.default_payment_methods
    )?.card;
  const isGettingValidSub = subDetails?.paymentMethods?.length === 0;
  // || isCardExpiringSoon(
  //   expiryDateChecker?.exp_month,
  //   expiryDateChecker?.exp_year
  // );
  const expCheck = isCardExpiringSoon(
    expiryDateChecker?.exp_month,
    expiryDateChecker?.exp_year
  );
  useEffect(() => {
    const dismissed = localStorage.getItem('dismissedPaymentDialog');
    const isCardEXPshown = localStorage.getItem('cardEXPDialog');
    const isPaidPlan = !!subDetails?.subscription && subDetails?.subscription?.id !== '6';
    const isSpclPlan = !!subDetails?.subscription && subDetails?.subscription?.id !== '14';
    const isGiftPlan = !!subDetails?.subscription && !['15', '16'].includes(subDetails?.subscription?.id);
    const pathNameNotToShow = window.location.pathname === '/settings/manage-payment-methods';
    const searchParamsCheck = new URLSearchParams(window.location.search);
    const isGiftFlowPage = searchParamsCheck.get('gift') === 'true'
      || searchParamsCheck.get('fromGift') === 'true'
      || window.location.pathname === '/gift-content'
      || window.location.pathname === '/gift'
      || window.location.pathname === '/gift-payment'
      || window.location.pathname === '/receiving-gift'
      || (window.location.pathname === '/cloud-upload' && !!sessionStorage.getItem('giftFlowCloudConnect'));
    // Onboarding / auth lanes ask for the card themselves — the quick-user flow
    // (setpassword → avatar → interview intro) ends on /quick-user-payment,
    // which is this exact request in a full page. Showing the dialog on top of
    // that lane is a duplicate ask, so it stays out of every onboarding route.
    const isOnboardingPage = isOnboardingPath(window.location.pathname);
    // Both dialogs read subDetails, which still describes the PREVIOUS account
    // for as long as it takes the switch to refetch — and showPaymentDialog has
    // no reset path, so a stale trigger sticks around after the fresh record
    // arrives. Only decide once the record belongs to the current token.
    const subIsForThisAccount = isSubscriptionForCurrentAccount(subDetails);
    if (
      !dismissed
      && !isGiftFlowPage
      && !isOnboardingPage
      && !isManagerImpersonating
      && !isBilledElsewhere
      && subIsForThisAccount
      // && fromLogin
      && isPaidPlan
      && isSpclPlan
      && isGiftPlan
      && isGettingValidSub
      // Same rule the no-card lock uses, so the two can never disagree: if we
      // don't force this account onto the payment page, we don't nag it here
      // either. Adds "not Lifetime" (paid once, forever — nothing to bill, and
      // the ids above never covered it), plus not-free and not-expired.
      && needsPaymentMethod(subDetails, allPlans)
    ) {
      setShowPaymentDialog(true);
    }
    setTimeout(() => {
      if (
        !isInActiveUser
        && !isGiftFlowPage
        && !isOnboardingPage
        && expCheck
        && !pathNameNotToShow
        && !isManagerImpersonating
        && !isBilledElsewhere
        && subIsForThisAccount
        // A Lifetime account is never billed again, so an expiring card on it
        // interrupts nothing. (This dialog needs a card to exist, so it can't
        // reuse needsPaymentMethod, which requires the opposite.)
        && !isLifetimePlan(subDetails, allPlans)
      ) {
        setCardExpiryDialog(true);
      }
    }, 100);
  }, [subDetails?.paymentMethods, window.location.pathname, isManagerImpersonating, isBilledElsewhere, allPlans]);

  const handleCloseDialog = () => {
    localStorage.setItem('dismissedPaymentDialog', 'true');
    setShowPaymentDialog(false);
    setFromLogin(false);
    paymentNavigation('/settings/manage-payment-methods?add=true');
  };
  const handleCloseCardEXPDialog = () => {
    localStorage.setItem('cardEXPDialog', 'true');
    setCardExpiryDialog(false);
    paymentNavigation('/settings/manage-payment-methods?add=true');
  };
  const closeDialog = () => {
    if (isGettingValidSub && subDetails?.subscription !== null) {
      localStorage.setItem('dismissedPaymentDialog', 'true');
      setShowPaymentDialog(false);
      setFromLogin(false);
    }
  };
  // useEffect(() => {
  //   document.addEventListener('dragover', dragOver);
  //   document.addEventListener('dragleave', dragLeave);
  //   return () => {
  //     document.removeEventListener('dragover', dragOver);
  //     document.removeEventListener('dragleave', dragLeave);
  //   };
  // }, []);
  const MultiSelect = () => {
    setOverlay(false);
    const files = Array.from(selectedFiles);
    const videoFiles = files.filter((file) => file.type.startsWith('video/'));
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    setVideoFiles(videoFiles);
    setImageFiles(imageFiles);
    revealUploadModal(videoFiles);
    setDroper(false);
  };
  useEffect(() => {
    if (selectedFiles?.length > 1) {
      MultiSelect();
    }
  }, [selectedFiles]);
  const handleDrop = (e) => {
    setOverlay(false);
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    const videoFiles = files.filter((file) => file.type.startsWith('video/'));
    const imageFiles = files.filter((file) => file.type.startsWith('image/'));
    setVideoFiles(videoFiles);
    setImageFiles(imageFiles);
    revealUploadModal(videoFiles);
    setDroper(false);
  };
  // const [cardNumberStripe, setCardNumberStripe] = useState('');
  // const [inputValue, setInputValue] = useState('');
  // const [expiryMonth, setExpiryMonth] = useState('');
  // const [expiryYear, setExpiryYear] = useState('');
  // const [cvc, setCvc] = useState('');
  // const [stripeToken, setStripeToken] = useState([]);
  // const [error, setError] = useState(null);
  // const callStripeAPI = async () => {
  //   const datax = new URLSearchParams();
  //   datax.append('card[number]', cardNumberStripe);
  //   datax.append('card[exp_month]', expiryMonth);
  //   datax.append('card[exp_year]', expiryYear);
  //   datax.append('card[cvc]', cvc);
  //   try {
  //     const response = await axios.post('https://api.stripe.com/v1/tokens', data, {
  //       headers: {
  //         'Content-Type': 'application/x-www-form-urlencoded',
  //         'Authorization': 'Bearer pk_live_51OyTOEKaFrbWdXakXgNskBU4TmXmqTIP6thT1vf8LjiR1FADapz2jX6Fi5a1h8KpG7hCAiEK9EnlrKbH7wDVGjOs00ReGilVdx',
  //       },
  //     });
  //     setStripeToken(response.data);
  //     setError(null);
  //   } catch (error) {
  //     setError(error.response?.data?.error?.message || 'Error occurred while contacting Stripe API');
  //     console.error('Error:', error);
  //   }
  // };
  useEffect(() => {
    if (navigateOnceClicked === true) {
      if (buttonText === 'Save to Draft') {
        navigateTo(`${Pathname.yourVideos}?tab=draft`);
      } else if (buttonText === 'Publish & Save' || buttonText === 'Publish') {
        navigateTo('/your-videos');
      }
    }
    setNavigateOnceClicked(false);
  }, [navigateOnceClicked]);
  const [storageSelectedType, setStorageSelectedType] = useState('name');
  const [storageSearchTerm, setStorageSearchTerm] = useState('');
  const [storageSelectedDate, setStorageSelectedDate] = useState('');
  const [storageTabTitle, setStorageTabTitle] = useState(0);
  const [storageSelectedItems, setStorageSelectedItems] = useState([]);
  const [videoThumbnails, setVideoThumbnails] = useState([]);
  const [videoThumbnails64, setVideoThumbnails64] = useState([]);

  const [activeTab, setActiveTab] = useState(0);
  const test = useSelector((state) => state.titleBulk);
  const [categoryRefreshTrigger, setCategoryRefreshTrigger] = useState(0);
  const [playlistRefreshTrigger, setPlaylistRefreshTrigger] = useState(0);
  const [newlyCreatedPlaylistId, setNewlyCreatedPlaylistId] = useState(null);
  const [newlyCreatedPlaylistTitle, setNewlyCreatedPlaylistTitle] = useState(null);
  const [subCategoryRefreshTrigger, setSubCategoryRefreshTrigger] = useState(0);
  const [newlyCreatedSubCategoryId, setNewlyCreatedSubCategoryId] = useState(null);

  // AWS S3 configuration
  AWS.config.update({
    region: awsConfig.region,
    accessKeyId: awsConfig.accessKey,
    secretAccessKey: awsConfig.secretKey,
  });
  AWS.config.httpOptions.timeout = 0;
  const s3 = new AWS.S3();

  // Helper variables for upload tracking
  const iteratorNum = [];
  const totalNum = [];
  let tempPercentages;

  // Get userId from userDetails
  const userId = userDetails?.isSubscription?.user_id;

  // Helper functions for upload process
  const updateUploadPercentagesModal = (draft_id, data) => {
    dispatch(saveUploadInfo({ [draft_id]: data }));
  };

  const closeToast = (draft_id, message, type) => {
    toast.update(draft_id, { closeButton: false, render: message, type: type === 'success' ? toast.TYPE.SUCCESS : toast.TYPE.ERROR, autoClose: 3000 });
    setUploadContainer((prevUploadContainer) => prevUploadContainer.filter((id) => id !== draft_id));
  };
  const updateItem = (percentage, draft_id, filename, uploadPercentages, updateUploadPercentagesModal) => {
    const rawPercent = (percentage / totalNum[draft_id]) * 100;
    const percent = Math.floor(rawPercent);
    toast.update(draft_id, { progress: rawPercent / 100 });
    const newPercentages = { ...uploadPercentages, [draft_id]: percent };
    tempPercentages = newPercentages;
    updateUploadPercentagesModal(draft_id, percent);
  };

  function formatFileSizeAccMb(sizeInBytes) {
    const megabyte = 1024 * 1024;
    const sizeInMB = sizeInBytes / megabyte;
    return sizeInMB.toFixed(2);
  }
  async function sendVideoDataToServer(theGreatPayload, draft_id) {
    const apiUrl = `${baseUrl}/V6/addVideo`;
    try {
      const response = await axios.post(apiUrl, theGreatPayload);
      // fetchContents(); // You may need to implement this if needed
      closeToast(draft_id, 'Uploading completed', 'success');
      autoSignIn();
      setUploadContainer2((prevUploadContainer) => prevUploadContainer?.filter((id) => id !== draft_id));
    } catch (error) {
      console.error('Error sending video data:', error);
    }
  }

  const handleUploadClick = (draft_id) => {
    const dataUpdate = { [`${draft_id}/status`]: 'DELETED' };
    update(ref(firebaseDatabase, 'status_from_sns'), dataUpdate);
    updateUploadPercentagesModal(draft_id, 'Cancelled');
    closeToast(draft_id, 'Upload has been canceled by user', 'error');
  };
  const updateDummy = (dummy, status = 0) => {
    if (status === 1) {
      dispatch(saveDummy({ [dummy?.draft_id]: dummy }));
    } else {
      dispatch(removeDummy(dummy));
    }
  };
  const handleEncouter = (draft_id) => {
    if (uploadQueue?.length <= 1) {
      if (photoList && photoList?.length > 0) {
        setActiveTab(1); // Switch to photo tab
      } else {
        setOpen(false); // Close modal only if no photos
      }
    }
    toast.update(draft_id, {
      closeButton: <CustomCloseloader />,
    });

    const databaseRef = ref(firebaseDatabase, 'status_from_sns');
    const changeFieldFromFirebase = (draft_id, statusString) => {
      const dataToUpdate = {
        [draft_id]: {
          status: statusString,
        },
      };
      update(databaseRef, dataToUpdate);
    };
    changeFieldFromFirebase(draft_id?.draft_id ? draft_id?.draft_id : draft_id, 'DELETED');
    updateDummy(draft_id?.draft_id ? draft_id?.draft_id : draft_id, 0);
    setIamUploading(false);
    setActiveUploads((prevUploads) => prevUploads?.filter((id) => id !== draft_id?.draft_id));
    setDraftOfUpload((prevUploads) => prevUploads?.filter((id) => id !== draft_id?.draft_id));
    setUploadContainer2((prevUploadContainer) => prevUploadContainer?.filter((id) => id !== draft_id?.draft_id));
    updateUploadPercentagesModal(draft_id?.draft_id ? draft_id?.draft_id : draft_id, 'Cancelled');
    closeToast(draft_id?.draft_id ? draft_id?.draft_id : draft_id, 'Upload has been canceled by user', 'error');
  };
  const handleToCloseUpload = (draft_id, updateUploadPercentagesModal) => {
    toast.update(draft_id, {
      closeButton: <CustomCloseDiv draft_id={draft_id} updateUploadPercentagesModal={updateUploadPercentagesModal} />,
    });
  };
  const CustomCloseButton = ({ draft_id }) => (
    <button className={styles.closeMeBro} onClick={() => handleToCloseUpload(draft_id)}>
      <CloseIcon />
    </button>
  );
  const backToOld = ({ draft_id, updateUploadPercentagesModal }) => {
    toast.update(draft_id, {
      closeButton: <CustomCloseButton draft_id={draft_id} updateUploadPercentagesModal={updateUploadPercentagesModal} />,
    });
  };
  const CustomCloseDiv = (draft_id, updateUploadPercentagesModal) => (
    <div className={styles.customDiv}>
      Are you sure you want to cancel ?
      <div className={styles.fleo}>
        <button className={styles.cancelPlz} onClick={() => backToOld(draft_id, updateUploadPercentagesModal)}>
          Exit
        </button>
        <button className={styles.noCancel} onClick={() => handleEncouter(draft_id)}>
          Continue to cancel
        </button>
      </div>
    </div>
  );
  const notify = (filename, draft_id, uploadPercentages, updateUploadPercentagesModal) => {
    const message = `Uploading ${filename}`;
    if (uploadPercentages[draft_id] === undefined) {
      tempPercentages = { ...uploadPercentages, [draft_id]: 'Initializing' };
      updateUploadPercentagesModal(draft_id, 'Starting Upload');
    }
    toast(message, {
      position: toast.POSITION.BOTTOM_RIGHT,
      autoClose: false,
      toastId: draft_id,
      closeButton: <CustomCloseButton draft_id={draft_id} updateUploadPercentagesModal={updateUploadPercentagesModal} />,
    });
  };
  const CustomCloseloader = () => (
    <button className={styles.closeMeBro}>
      <Spinner />
    </button>
  );
  async function initMultipartUpload(bucket, key) {
    const params = {
      Bucket: bucket,
      Key: key,
    };
    try {
      const response = await s3.createMultipartUpload(params).promise();
      return response;
    } catch (error) {
      console.error('Error initiating multipart upload:', error);
      throw error;
    }
  }

  async function uploadPart(bucket, key, partNumber, partBody, uploadId, setVideoUploadedParts, filename, draft_id, uploadPercentages, updateUploadPercentagesModal, partSize, startTime, fileSize) {
    const params = {
      Bucket: bucket,
      Key: key,
      PartNumber: partNumber,
      UploadId: uploadId,
      Body: partBody,
    };
    return s3.uploadPart(params)
      .promise()
      .then((data) => {
        const currentTime = new Date().getTime();
        const elapsedSize = (partNumber * partSize) / 1024 / 1024;
        const elapsedTime = (currentTime - startTime) / 1000;
        const remainingSize = (fileSize / 1024 / 1024) - elapsedSize;
        updateUploadDetails(draft_id, { elapsed_size: elapsedSize, elapsed_time: elapsedTime, remaining_size: remainingSize > 0 ? remainingSize : 0 });
        iteratorNum[draft_id] += 1;
        updateItem(iteratorNum[draft_id], draft_id, filename, uploadPercentages, updateUploadPercentagesModal);
        setVideoUploadedParts((prevState = []) => [...prevState, partNumber]);
        return data;
      })
      .catch((err) => {
        console.log(`Error uploading part ${partNumber}:`, err);
        throw err;
      });
  }

  async function uploadPartSequentially(file, bucket, key, uploadId, partNumber, start, partSize, parts, draft_id, startTime) {
    const databaseRef = ref(firebaseDatabase, 'status_from_sns');
    const data = {};
    const dataCallback = (snapshot) => {
      if (snapshot.exists()) {
        const dataObj = snapshot.val();
        data[draft_id] = dataObj[draft_id];
      }
    };
    onValue(databaseRef, dataCallback);
    const unsubscribe = () => {
      off(databaseRef, 'value', dataCallback);
    };
    const end = Math.min(start + partSize, file.size);
    if (start < file.size && data[draft_id]?.status !== 'DELETED') {
      await uploadPart(getBucketName(userId), key, partNumber, file.slice(start, end), uploadId, setVideoUploadedParts, file.name, draft_id, uploadPercentages, updateUploadPercentagesModal, partSize, startTime, file.size)
        .then((item) => {
          parts.push({ PartNumber: partNumber, ETag: item.ETag });
        })
        .catch((error) => {
          console.error('Upload part error:', error);
          unsubscribe();
        });
      unsubscribe();
      await uploadPartSequentially(file, bucket, key, uploadId, partNumber + 1, end, partSize, parts, draft_id, startTime);
    }
  }

  async function completeMultipartUpload(bucket, key, parts, uploadId, file, index) {
    const params = {
      Bucket: bucket,
      Key: key,
      MultipartUpload: {
        Parts: parts,
      },
      UploadId: uploadId,
    };
    try {
      const response = await s3.completeMultipartUpload(params).promise();
      let videoDuration = 0;
      let videoHeightI = 0;
      let videoWidthI = 0;
      try {
        const videoElement = document.createElement('video');
        videoElement.src = URL.createObjectURL(file);
        await videoElement?.load();
        videoElement.muted = true;
        // Wait for the metadata to load to access duration, height, and width
        await new Promise((resolve, reject) => {
          videoElement.addEventListener('loadedmetadata', () => {
            videoDuration = videoElement.duration;
            videoHeightI = videoElement.videoHeight;
            videoWidthI = videoElement.videoWidth;
            resolve();
          });
          videoElement.addEventListener('error', reject);
        });
        try {
          await videoElement.play();
        } catch (playError) {
          console.warn('Video play was prevented:', playError);
        }
        videoElement?.pause();
      } catch (e) {
        console.error('Error in getting width and height:', e);
      }
      const formData = new FormData();
      const s3Url = generateS3Url();
      formData.append('token', getToken());
      formData.append('draft_id', file?.draftId);

      // Handle thumbnail - convert noPhotoAvailable to blob if needed
      if (videoThumbnails && videoThumbnails[file?.draftId] && videoThumbnails[file?.draftId][0]) {
        formData.append('thumbnail', videoThumbnails[file?.draftId][0]);
      } else {
        // Convert the default image to blob
        try {
          const response = await fetch(ForNoImage);
          const blob = await response.blob();
          formData.append('thumbnail', blob, 'ForNoImage.png');
        } catch (error) {
          console.error('Error converting default thumbnail to blob:', error);
          // Fallback to original approach if fetch fails
          formData.append('thumbnail', ForNoImage);
        }
      }
      if (videoThumbnails && videoThumbnails[file?.draftId] && Array.isArray(videoThumbnails[file?.draftId])) {
        videoThumbnails[file?.draftId].forEach((thumbnail) => {
          formData.append('thumbnails[]', thumbnail);
        });
      }
      formData.append('title', test && test[file?.draftId] ? test[file?.draftId] : '');
      formData.append('video_duration', videoDuration);
      formData.append('videoOreintation', videoHeightI < videoWidthI ? 1 : 2);
      formData.append('videoHeight', videoHeightI);
      formData.append('videoWidth', videoWidthI);
      selectedItemsInBulk[index].category ? formData.append('category_id', selectedItemsInBulk[index].category) : null;
      formData.append('playlist_ids', selectedItemsInBulk[index]?.playlist?.map((item) => item?.value));
      formData.append('visibility', selectedItemsInBulk[index].visibility);
      formData.append('group_ids', selectedItemsInBulk[index]?.group_ids);
      formData.append('s3_url', `${s3Url}${getBucketNamePath(userId)}/${key}`);
      formData.append('s3_image_bucket_url', s3Url);
      formData.append('s3_status', 'Completed');
      formData.append('bucket_folder_path', `${getBucketNamePath(userId)}/${key}`);
      formData.append('video_size', formatFileSizeAccMb(file.size));
      closeToast(file?.draftId, 'Uploading completed', 'success');
      sendVideoDataToServer(formData, file?.draftId);
      if (subscriptionDetails?.current_plan === 'Free') {
        autoSignIn();
      }
      if (uploadQueue?.length <= 1) {
        if (photoList && photoList.length > 0) {
          setActiveTab(1); // Switch to photo tab
        } else {
          // setOpen(false); // Close modal only if no photos
        }
      }
      return response;
    } catch (error) {
      console.error('Error completing multipart upload:', error);
      if (subscriptionDetails?.current_plan === 'Free') {
        autoSignIn();
      }
      throw error; // Rethrow the error to be handled by the caller
    }
  }
  // console.log('videoThumbnails', videoThumbnails, videoThumbnails64);
  async function uploadLargeFile(bucket, key, file, draft_id, index, fileName) {
    setUploadContainer((prevUploadContainer) => [...prevUploadContainer, draft_id]);
    setUploadContainer2((prevUploadContainer) => [...prevUploadContainer, draft_id]);
    try {
      notify(file.name, draft_id, uploadPercentages, updateUploadPercentagesModal);
      const { UploadId } = await initMultipartUpload(bucket, fileName);
      const minimumPartSize = 5 * 1024 * 1024; // 5MB
      let partSize = file.size / 100;
      if (partSize < minimumPartSize) {
        partSize = minimumPartSize;
      }
      const fileSize = file.size;
      const partCount = Math.ceil(fileSize / partSize);
      const startTime = new Date().getTime();
      const parts = [];
      iteratorNum[draft_id] = 0;
      totalNum[draft_id] = partCount;
      await uploadPartSequentially(file, bucket, fileName, UploadId, 1, 0, partSize, parts, draft_id, startTime);
      const databaseRef = ref(firebaseDatabase, 'status_from_sns');
      const data = {};
      const dataCallback = (snapshot) => {
        if (snapshot.exists()) {
          const dataObj = snapshot.val();
          data[draft_id] = dataObj[draft_id];
        }
      };
      onValue(databaseRef, dataCallback);
      const unsubscribe = () => {
        off(databaseRef, 'value', dataCallback);
      };
      if (data[draft_id]?.status !== 'DELETED') {
        const uploadedData = await completeMultipartUpload(bucket, fileName, parts, UploadId, file, index);
        const newPercentages = { ...uploadPercentages, [draft_id]: 'Please wait' };
        tempPercentages = newPercentages;
        updateUploadPercentagesModal(draft_id, 'Please wait');
        iteratorNum[file.name] = 0;
        totalNum[draft_id] = 0;
        setIamUploading(true);
      } else {
        updateUploadPercentagesModal(draft_id, 'Cancelled');
        closeToast(draft_id, 'Upload has been canceled by user', 'error');
        unsubscribe();
      }
      unsubscribe();
    } catch (error) {
      updateUploadPercentagesModal(draft_id, 'Cancelled');
      closeToast(draft_id, 'Upload has been canceled by user', 'error');
      const newPercentages = { ...uploadPercentages, [draft_id]: 'Cancelled' };
      tempPercentages = newPercentages;
      updateUploadPercentagesModal(draft_id, 'Cancelled');
      updateUploadDetails(draft_id, { elapsed_size: 0, elapsed_time: 0, remaining_size: 0 });
      const allPropertiesAreNumericOrString = Object.values(uploadPercentages).some((value) => typeof value === 'number' || value === 'Starting Upload' || value === 'Initializing');
      if (allPropertiesAreNumericOrString) {
        setIamUploading(false);
      } else setIamUploading(true);
    }
  }

  async function processUploadQueue() {
    if (activeUploads.length < 5 && uploadQueue.length > 0) {
      // Get the next file from the queue
      const nextUpload = uploadQueue[0];

      // Start the upload and remove it from the queue
      setActiveUploads((prevUploads) => [...prevUploads, nextUpload.draftId]);
      setUploadQueue((prevQueue) => prevQueue.slice(1));

      await uploadLargeFile(getBucketName(userId), nextUpload.file.name, nextUpload.file, nextUpload.draftId, nextUpload.index, nextUpload.fileName);
      // Remove from active uploads once completed
      setActiveUploads((prevUploads) => prevUploads.filter((id) => id !== nextUpload.draftId));
    }
  }
  useEffect(() => {
    if (uploadQueue.length > 0 && activeUploads.length < 5) {
      processUploadQueue();
    }
  }, [uploadQueue, activeUploads]);

  // Download control handlers
  const handleCancelAllDownloads = useCallback(() => {
    // Set cancel flag for bulk download loop to check
    bulkDownloadCancelRef.current = true;

    // Abort all active download controllers
    downloadAbortControllersRef.current.forEach((controller) => {
      try {
        controller.abort();
      } catch (error) {
        // Ignore errors
      }
    });
    downloadAbortControllersRef.current.clear();
    pausedDownloadsRef.current.clear();
    resumeTriggersRef.current.clear();
    setDownloadingFiles([]);
    setDownloadBox(false);
    setDownloadBatchProgress({ current: 0, total: 0 });

    // Re-enable download button in YourVideos
    setIsBulkDownloading(false);
  }, [setDownloadingFiles, setDownloadBox, setDownloadBatchProgress]);

  const handleCancelDownload = useCallback((downloadId) => {
    const controller = downloadAbortControllersRef.current.get(downloadId);
    if (controller) {
      try {
        controller.abort();
      } catch (error) {
        // Ignore errors
      }
      downloadAbortControllersRef.current.delete(downloadId);
    }
    pausedDownloadsRef.current.delete(downloadId);
    setDownloadingFiles((prev) => prev.filter((f) => f.id !== downloadId));
  }, [setDownloadingFiles]);

  const handlePauseDownload = useCallback((downloadId) => {
    // Add to ref first (synchronous) to ensure immediate effect
    pausedDownloadsRef.current.add(downloadId);

    // Update UI state
    setDownloadingFiles((prev) => prev.map((f) => (f.id === downloadId
      ? { ...f, status: 'paused' }
      : f)));
  }, [setDownloadingFiles]);

  const handleResumeDownload = useCallback((downloadId) => {
    // Remove from paused ref first (synchronous)
    pausedDownloadsRef.current.delete(downloadId);

    // Update UI state
    setDownloadingFiles((prev) => prev.map((f) => (f.id === downloadId
      ? { ...f, status: 'downloading' }
      : f)));

    // If there's a resume trigger function registered, call it to restart the download
    const resumeTrigger = resumeTriggersRef.current.get(downloadId);
    if (resumeTrigger) {
      resumeTrigger();
      resumeTriggersRef.current.delete(downloadId);
    }
  }, [setDownloadingFiles]);

  // console.log('selectedFiles', showMultipleModal, selectedFilesMulti);
  return (
    <AuthContext.Provider
      value={{
        forceAutoSignin,
        getToken,
        preferredEmail,
        removeToken,
        userIsSignedIn,
        setPreferredEmail,
        preferredUserDetails,
        setPreferredUserDetails,
        setToken,
        setUserSignInStatus,
        setPlansPage,
        plansPage,
        isInActiveUser,
        gettingSubscription,
        getEmailVerified,
        setEmailVerified,
        removeEmailVerified,
      }}
    >
      <AppContext.Provider
        value={{
          setOpenPaymentSuccess,
          formatFileSizeAccMb,
          warningTitle,
          warningMessage,
          totalOnGoingUploadSize,
          freePlanLimitMb,
          hasReachedStorageLimit,
          selectedFilesMulti,
          setSelectedFilesMulti,
          showMultipleModal,
          setShowMultipleModal,
          showAddPlaylistModal,
          setShowAddPlaylistModal,
          showAddCategoryModal,
          setShowAddCategoryModal,
          categoryModalEditData,
          setCategoryModalEditData,
          openNewUploadModal,
          setOpenNewUploadModal,
          openPhotoUploadModal,
          setOpenPhotoUploadModal,
          photoAlbumModalOpen,
          setPhotoAlbumModalOpen,
          pendingCloudPhotos,
          setPendingCloudPhotos,
          pendingCloudPhotoService,
          setPendingCloudPhotoService,
          base64ToBinary,
          activeTab,
          setActiveTab,
          videoThumbnails,
          setVideoThumbnails,
          videoThumbnails64,
          setVideoThumbnails64,
          findAllVideoCategory,
          setFindAllVideoCategory,
          selectedItemsInBulk,
          setSelectedItemsInBulk,
          processUploadQueue,
          IsfreePlan,
          MAX_SIZE_GB,
          chooseAlbumStep,
          setChooseAlbumStep,
          photoList,
          setPhotoList,
          options,
          setOptions,
          setUploadModalVisibility,
          setImageFiles,
          imageFiles,
          loginWithSocial,
          setLoginWithSocial,
          popUpModalVisible,
          setPopUpModalVisible,
          uploadType,
          setUpdateType,
          isEmailAllowed,
          allowedEmails,
          setAllowedEmails,
          clearChecked,
          stripeUiSelector,
          setStripeUiSelector,
          checked,
          setChecked,
          setOpen,
          videoFiles,
          setVideoFiles,
          pendingVideoFile,
          setPendingVideoFile,
          pendingPhotoFiles,
          setPendingPhotoFiles,
          screatId,
          setScreatId,
          defaultCategoryName,
          setOpenPaymentLoader,
          setCardExpiryDialog,
          isIOS,
          categoryDetails,
          tabTitle,
          setTabTitle,
          setCategoryDetails,
          albumDetails,
          setAlbumDetails,
          storageSelectedType,
          navigateToInviteFirstTime,
          setStorageSelectedType,
          storageSearchTerm,
          setStorageSearchTerm,
          storageSelectedDate,
          setStorageSelectedDate,
          storageTabTitle,
          setStorageTabTitle,
          storageSelectedItems,
          setStorageSelectedItems,
          categoryRefreshTrigger,
          setCategoryRefreshTrigger,
          playlistRefreshTrigger,
          setPlaylistRefreshTrigger,
          newlyCreatedPlaylistId,
          setNewlyCreatedPlaylistId,
          newlyCreatedPlaylistTitle,
          setNewlyCreatedPlaylistTitle,
          showAddSubCategoryModal,
          setShowAddSubCategoryModal,
          subCategoryRefreshTrigger,
          setSubCategoryRefreshTrigger,
          newlyCreatedSubCategoryId,
          setNewlyCreatedSubCategoryId,
          setFromLogin,
          isGettingValidSub,
          planPurchasing,
          setPlanPurchasing,
          setOpenX,
          cloudToken,
          setCloudToken,
          openX,
          expressloading,
          orderArrange,
          setOrderArrange,
          fetchLayoutOrder,
          affliatePlanId,
          setAffliatePlanId,
          setExpressLoading,
          isFromAffl,
          setisFromAffl,
          selectedPlan,
          selectPlan,
          backendUrl,
          expressCheckout,
          cardUpdateBoolean,
          setcardUpdateBoolean,
          trailModalVisible,
          setTrailPopUpModalVisible,
          uploadContainer2,
          setUploadContainer2,
          cardPayId,
          setcardPayId,
          cardUpdate,
          setcardUpdate,
          trialVal,
          setTrial,
          didIOpenModal,
          allvideoData,
          setAllvideoData,
          isCompleted,
          setisCompleted,
          firstTimeOnlychecked,
          setFirstTimeOnlyChecked,
          cardAdded,
          setcardAdded,
          navigateOnceClicked,
          buttonText,
          setNavigateOnceClicked,
          MAX_VIDEOS,
          MAX_PHOTOS,
          trailVideos,
          activeModalId,
          setActiveModalId,
          isUploadingBulky,
          setIsUploadingBulky,
          uploadQueue,
          setUploadQueue,
          activeUploads,
          setActiveUploads,
          draftOfUpload,
          setDraftOfUpload,
          totalDraft,
          uploadProcessing,
          setUploadProcessing,
          setdidIOpenModal,
          disableDrag,
          setdisableDrag,
          isTrail,
          setIsTrail,
          removeProjectorTextChange,
          setRemoveProjectorTextChange,
          dataOfCategory,
          profileDetails,
          addMorePhoto,
          videoGroup,
          setVideoGroup,
          selectedFiles,
          setSelectedFiles,
          setaddMorePhoto,
          setfetchDraftID,
          setProfileDetails,
          setFormData,
          isAddDraftDone,
          pubLoader,
          cardNumber,
          profileLoader,
          setProfileLoader,
          setCardNumber,
          cvvNumber,
          setCvvNumber,
          expirationDate,
          setExpirationDate,
          setPubLoader,
          setisAddDraftDone,
          draftCategoryState,
          setDraftCategoryState,
          isS3UploadComplete,
          setS3UploadComplete,
          setdataOfCategory,
          selectedItems,
          setSelectedItems,
          singleEdit,
          encounterLoading,
          loader,
          setLoader,
          setEncounterLoading,
          needToShowETA,
          setNeedToShowETA,
          setSingleEdit,
          setUploadContainer,
          uploadContainer,
          uploaderLoader,
          setUploaderLoader,
          holdWhileDelete,
          setholdWhileDelete,
          referralCode,
          visible,
          setVisible,
          textOfUpload,
          setLoadingState,
          contextLoading,
          setContextLoading,
          profileName,
          setProfileName,
          amICalled,
          setamICalled,
          settextOfUpload,
          setHelpNeed,
          s3Data,
          fetchData,
          brandLogoUrl,
          brandName,
          fetchBrandConfig,
          invalidErr,
          setinvalidErr,
          isInvalid,
          setIsInvalid,
          apiResponse,
          isLoading,
          topOrBottom,
          setTopOrBottom,
          autoSignIn,
          uploadPercentagesInNumber,
          setUploadPercentagesInNumber,
          getAccessChecker,
          setGetAccessChecker,
          iamUploading,
          setIamUploading,
          isTokenChange,
          setIsTokenChange,
          setS3Data,
          affiliateCode,
          setAffiliatedCode,
          handleManger,
          lineReq,
          setLineReq,
          messaged,
          setmessaged,
          mangerFunctionData,
          setmangerFunctionData,
          handleClick,
          switchingFunction,
          bigUserData,
          setbigUserData,
          setMangerToken,
          mangerToken,
          userAccessedData,
          managerPrivatePlanAccess,
          managerPaymentMethods,
          setManagerPaymentMethods,
          setHoldTheWeb,
          toastForGroup,
          setToastForGroup,
          getUserConnections,
          gettingUserConnections,
          setAppSnackbar,
          setUserConnections,
          setUserDetails,
          userConnections,
          myProjectorSettings,
          setMyProjectorSettings,
          userDetails,
          subscriptionDetails,
          getSubscription,
          setPlansPage,
          plansPage,
          currentSelectedPlan,
          setCurrentSelectedPlan,
          langText,
          setReferralStatus,
          referralStatus,
          isViewerListUpdated,
          setViewerListUpdate,
          showProgressBox,
          setProgressBox,
          showDownloadBox,
          setDownloadBox,
          downloadingFiles,
          setDownloadingFiles,
          isBulkDownloading,
          setIsBulkDownloading,
          downloadBatchProgress,
          setDownloadBatchProgress,
          downloadAbortControllersRef,
          pausedDownloadsRef,
          resumeTriggersRef,
          bulkDownloadCancelRef,
          onDownloadPanelVisibleRef,
          handleCancelAllDownloads,
          handleCancelDownload,
          handlePauseDownload,
          handleResumeDownload,
          uploadingVideo,
          setUploadingVideo,
          uploadingVideoDetails,
          setUploadingVideoDetails,
          uploadProgress,
          setUploadProgress,
          visibility,
          setVisibility,
          flowEventHandler,
          startingFlowEvent,
          isSingleUpload,
          eta,
          closeModal,
          apiCALL,
          setapiCALL,
          setSingleUpload,
          pageLoad,
          removeToken,
          setPageLoad,
          setVideoCount,
          videoCount,
          errorMsg,
          setModalVisibility,
          setUniqueId,
          setAllPlans,
          allPlans,
          selectedUploadFiles,
          setSelectedUploadFiles,
          draftContent,
          setDraftContent,
          setVideoUploadedParts,
          daysOfMonths,
          refreshContent,
          setRefreshContent,
          uploadPercentages,
          updateUploadPercentages,
          firebaseDatabase,
          setUploadingInProgress,
          uploadingInProgress,
          photoAlbumLoader,
          setphotoAlbumLoader,
          theGrtLoader,
          settheGrtLoader,
          categoryLoader,
          setcategoryLoader,
        }}
      >
        {openPaymentLoader && (
          <RotatingLoader
            colour="white"
            text="Processing Payment..."
            background="radial-gradient(circle at center,#1f2330,#07080c)"
          />
        )}
        <div
          className={styles.app}
          onDrop={disableDrag === true ? handleDrop : handleSingleDrop}
          onDragOver={(e) => dragOver(e)}
          onDragLeave={(e) => dragLeave(e)}
        >
          {showPaymentDialog && !suppressCardDialogs && (
            <CardExp
              handleClose={handleCloseDialog}
              title="Unlock your Projector"
              buttonText="Update Payment method now"
              contentText="Please update your payment method or your account will be suspended and your content will be at your risk for deletion."
            />
          )}
          {cardExpiryDialog && !suppressCardDialogs && (
            <CardExp
              title="Unlock your Projector"
              contentText="Please update your payment method as your subscriptions maybe interrupted."
              buttonText="Update Payment method now"
              handleClose={handleCloseCardEXPDialog}
            />
          )}
          {openPaymentSuccess && (
            <SuccessPopUp
              expressData={expressData}
              paymentNavigation={paymentNavigation}
              paymentStatus={paymentStatus}
              setOpenPaymentLoader={setOpenPaymentLoader}
              isSwitchSuccess={isSwitchSuccess}
              onHide={() => {
                setOpenPaymentSuccess(false);
                setIsSwitchSuccess(false);
              }}
            />
          )}
          {autoSigningIn || loadingForAccess ? (
            <div className={styles.autoSignInSpinnerContainer}>
              <span className={styles.appLoaderCaption}>
                <CircularProgress size={18} thickness={4} sx={{ color: '#5aa5ef' }} />
                Getting your Projector ready
              </span>
            </div>
          ) : (
            <Router>
              <RouteSwitch
                {...{
                  setUserSignInStatus,
                  isNewUser: userDetails?.isNewUser,
                  userIsSignedIn,
                  isInActiveUser,
                  isEmailAllowed
                }}
              />
            </Router>
          )}
        </div>

        <Snackbar
          autoHideDuration={appSnackbar?.duration}
          isVisible={appSnackbar?.isVisible}
          message={appSnackbar?.message || ''}
          onClose={() => {
            setAppSnackbar(defaultAppSnackbar);
          }}
          // Undefined for every existing caller, so the app toast stays top-centre.
          // A caller can pass placement in its setAppSnackbar payload to opt one
          // message into a bottom slot — /setpassword does, so its success toast
          // matches the bottom toasts that page already shows.
          placement={appSnackbar?.placement}
          type={appSnackbar?.type}
        />
        {/* Old Upload ETA Panel - Only show when using legacy upload system */}
        {!UPLOAD_CONFIG.USE_NEW_UPLOAD_SYSTEM && needToShowETA === true && uploadContainer?.length > 0 && (
          <div className={styles.fullSpeed}>
            {!Number.isNaN(eta) && (
              <div className={styles.Sppped}>
                {eta > 1
                  ? `${eta} Minutes remaining`
                  : eta <= 1
                    ? 'Less than a minute'
                    : 'Calculating'}
              </div>
            )}
            <button
              className={styles.hideOrShow}
              onClick={() => setToastVisiblity(!toastVisiblity)}
            >
              {toastVisiblity !== true ? (
                <img src={DownArrow} alt="" />
              ) : (
                <img src={UpArrow} alt="" />
              )}
            </button>
          </div>
        )}
        {/* { droper && <DropMe dragLeave={dragLeave} dragOver={dragOver} handleDrop={handleDrop} />} */}
        {open && (
          <ListingModal
            buttonText={buttonText}
            setButtonText={setButtonText}
            setOpen={() => setOpen(false)}
            setEditLoader={setEditLoader}
            videoFiles={videoFiles}
            setVideoFiles={setVideoFiles}
          />
        )}
        {/* {uploadModalIsVisible && (
          <UploadModal
            isVisible={uploadModalIsVisible}
            onHide={hideUploadModal}
            imageFiles={imageFiles}
          />
        )} */}
        <ToastContainer
          closeOnClick={false}
          className={
            toastVisiblity !== true
              ? styles.toastContainerShow
              : styles.toastContainerNone
          }
        />
        {/* New Upload Panel - Shows when using new upload system (hidden on gift flow - has its own progress UI) */}
        {UPLOAD_CONFIG.USE_NEW_UPLOAD_SYSTEM && UPLOAD_CONFIG.SHOW_UPLOAD_PANEL && !window.location.pathname.startsWith('/gift') && !window.location.pathname.startsWith('/mobile-upload') && !window.location.pathname.startsWith('/contribute') && (
          <UploadPanel />
        )}

        {/* Add Playlist Modal - Available throughout the app */}
        {showAddPlaylistModal && (
          <AddPlaylistModal
            open={showAddPlaylistModal}
            onClose={() => setShowAddPlaylistModal(false)}
            onPlaylistCreated={(newPlaylist) => {
              setShowAddPlaylistModal(false);
              setNewlyCreatedPlaylistId(newPlaylist?.id || null);
              setNewlyCreatedPlaylistTitle(newPlaylist?.title || null);
              setPlaylistRefreshTrigger((prev) => prev + 1);
            }}
          />
        )}

        {/* Create/Edit Category Modal - Available throughout the app */}
        {showAddCategoryModal && (
          <CreateCategoryPage
            controlled
            initialOpen
            editMode={!!categoryModalEditData}
            editData={categoryModalEditData}
            onModalClose={() => {
              setShowAddCategoryModal(false);
              setCategoryModalEditData(null);
            }}
            onCategoryCreated={() => setCategoryRefreshTrigger((prev) => prev + 1)}
          />
        )}

        {/* Add SubCategory Modal - Available throughout the app */}
        {showAddSubCategoryModal && (
          <AddSubCategoryModal
            open={showAddSubCategoryModal}
            onClose={() => setShowAddSubCategoryModal(false)}
            onSubCategoryCreated={(newSubCategory) => {
              setShowAddSubCategoryModal(false);
              setNewlyCreatedSubCategoryId(newSubCategory?.id || null);
              setSubCategoryRefreshTrigger((prev) => prev + 1);
            }}
          />
        )}
      </AppContext.Provider>
    </AuthContext.Provider>
  );
};
